import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../utils/axios';
import {
  EMPTY_SHOP_CATALOG,
  prefetchCheckoutQuote,
  prefetchShopCatalog,
  prefetchShopProduct,
  productSlugFromPath,
  readCachedCatalog,
  readCachedProduct,
  readCachedQuote,
} from '../utils/shopSpeed';
import SeoHead from './SeoHead';
import PreferredSourceButton from './PreferredSourceButton';
import MarketplaceLogo from './MarketplaceLogo';
import taaounityLogo from '../logos/taaounity-mark.png';
import { QRCodeSVG } from 'qrcode.react';
import { articleCoopPath, ARTICLE_COOP_LIST, ARTICLE_BLOG_LIST } from '../utils/articlePaths';
import { buildAbsoluteUrl, buildMarketplaceBrowseJsonLd, buildMarketplaceCoopJsonLd, buildMarketplaceHomeJsonLd, buildMarketplaceProductJsonLd, MARKETPLACE_BROWSE_SEO } from '../utils/seo';
import { ShopI18nContext, bilingualShopCopy, useShopI18n } from '../utils/shopI18n';
import {
  addLocalCollection,
  createShopCollection,
  fetchShopCollections,
  fetchShopFollows,
  fetchShopMe,
  fetchShopOrders,
  followShopCoop,
  getShopCustomer,
  isShopLoggedIn,
  saveShopProduct,
  shopLogin,
  shopLogout,
  shopRegister,
  unfollowShopCoop,
  unsaveShopProduct,
} from '../utils/shopAuth';
import { trackMetaAddToCart, trackMetaPurchase, trackMetaViewContent } from '../utils/metaPixel';
import './marketplace.css';

function collectCooperatives(catalog, stories = [], coopLabel = 'Coopérative') {
  const fromCatalog = (catalog.cooperatives || []).map((coop) => ({
    type: 'coop',
    id: `coop-${coop.slug || coop.id}`,
    name: coop.name,
    slug: coop.slug,
    hint: coop.region || coopLabel,
    image: coop.image_url || '',
  }));
  const fromProducts = (catalog.products || [])
    .filter((product) => product.cooperative_name)
    .map((product) => ({
      type: 'coop',
      id: `coop-${product.cooperative_slug || product.cooperative_name}`,
      name: product.cooperative_name,
      slug: product.cooperative_slug || '',
      hint: coopLabel,
      image: product.cooperative_logo_url || '',
    }));
  const fromStories = stories.map((story) => ({
    type: 'coop',
    id: `story-${story.slug}`,
    name: story.cooperative_name || story.title,
    slug: story.slug,
    hint: story.region || story.craft || coopLabel,
    image: story.cover_url || story.image_url || '',
    storySlug: story.slug,
  }));
  const seen = new Set();
  return [...fromCatalog, ...fromProducts, ...fromStories].filter((item) => {
    const key = (item.slug || item.name || '').toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildHomeSuggestions(catalog, cooperatives, query, labels = {}) {
  const q = query.trim().toLowerCase();
  const categories = (catalog.categories || []).map((cat) => ({
    type: 'category',
    id: `cat-${cat.slug}`,
    name: cat.name,
    slug: cat.slug,
    hint: labels.category || 'Catégorie',
    image: cat.image_url || '',
  }));
  const products = (catalog.products || []).map((product) => ({
    type: 'product',
    id: `p-${product.slug}`,
    name: product.title,
    slug: product.slug,
    hint: product.cooperative_name || product.category_name || labels.product || 'Produit',
    image: product.image_url || '',
  }));
  if (!q) {
    const featured = products.filter((item) =>
      (catalog.products || []).find((product) => product.slug === item.slug)?.is_featured
    );
    const preload = [
      ...categories.slice(0, 4),
      ...cooperatives.slice(0, 3),
      ...(featured.length ? featured : products).slice(0, 4),
    ];
    return preload.slice(0, 10);
  }
  return [...categories, ...cooperatives, ...products]
    .filter((item) => `${item.name} ${item.hint || ''}`.toLowerCase().includes(q))
    .slice(0, 8);
}

const CART_KEY = 'taa_shop_cart';

function readCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('taa-shop-cart'));
}

const SAVED_KEY = 'taa-shop-saved';

function readSaved() {
  try {
    const list = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
    return Array.isArray(list) ? list.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeSaved(slugs) {
  localStorage.setItem(SAVED_KEY, JSON.stringify(slugs));
  window.dispatchEvent(new Event('taa-shop-saved'));
}

function toggleSaved(slug) {
  if (!slug) return false;
  const list = new Set(readSaved());
  if (list.has(slug)) list.delete(slug);
  else list.add(slug);
  writeSaved([...list]);
  return list.has(slug);
}

function showSaveToast(product) {
  if (!product?.slug) return;
  window.dispatchEvent(new CustomEvent('taa-shop-save-toast', { detail: { product } }));
}

function saveOrUnsave(product) {
  if (!product?.slug) return false;
  const wasSaved = readSaved().includes(product.slug);
  const next = toggleSaved(product.slug);
  if (wasSaved) {
    if (isShopLoggedIn()) {
      unsaveShopProduct(product.slug).catch(() => {});
    }
    return false;
  }
  showSaveToast(product);
  if (isShopLoggedIn()) {
    saveShopProduct(product.slug).catch(() => {});
  }
  return next;
}

function browseCategoryPath(slug) {
  return slug ? `/marketplace/browse/${slug}` : '/marketplace/browse';
}

function dealsCategoryPath(slug) {
  return slug ? `/marketplace/deals/${slug}` : '/marketplace/deals';
}

function coopShopPath(slug) {
  return slug ? `/marketplace/c/${slug}` : '/marketplace/browse';
}

const FOLLOWED_COOPS_KEY = 'taa-shop-followed-coops';

function readFollowedCoops() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(FOLLOWED_COOPS_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeFollowedCoops(slugs) {
  window.localStorage.setItem(FOLLOWED_COOPS_KEY, JSON.stringify(slugs));
}

function cacheFollowedSlug(slug, following) {
  if (!slug) return following;
  const current = readFollowedCoops();
  const next = following
    ? [...current.filter((item) => item !== slug), slug]
    : current.filter((item) => item !== slug);
  writeFollowedCoops(next);
  window.dispatchEvent(new Event('taa-shop-follows'));
  return following;
}

function showFollowToast(name) {
  window.dispatchEvent(new CustomEvent('taa-shop-follow-toast', { detail: { name: name || '' } }));
}

function productHasPromo(product, deals = []) {
  if (discountPercent(product) > 0) return true;
  return deals.some(
    (deal) =>
      deal.product_slug === product.slug ||
      deal.product_detail?.slug === product.slug ||
      (deal.product && String(deal.product) === String(product.id))
  );
}

function categoryBlurb(category, t) {
  if (!category) return '';
  if (category.description) return category.description;
  return t.categoryBlurb(category.name);
}

function sortBrowseProducts(products, sort) {
  const list = [...products];
  if (sort === 'price-asc') return list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
  if (sort === 'price-desc') return list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
  if (sort === 'sold') {
    return list.sort((a, b) => {
      const sold = Number(b.review_count || 0) - Number(a.review_count || 0);
      if (sold) return sold;
      return Number(b.rating_avg || 0) - Number(a.rating_avg || 0);
    });
  }
  return list.sort((a, b) => {
    const newest = new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime();
    if (newest) return newest;
    return Number(b.id) - Number(a.id) || String(b.slug || '').localeCompare(String(a.slug || ''));
  });
}

function formatPrice(product) {
  if (product == null || product.price == null) return '';
  const amount = Number(product.price);
  const currency = product.currency || 'MAD';
  return `${amount.toFixed(2)} ${currency}`;
}

function SocialProofToasts({ items = [], hidden }) {
  const { t, raw } = useShopI18n();
  const list = (items || []).filter((item) => item?.name && item?.product_title);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setIndex(0);
    setVisible(list.length > 0);
  }, [list.length]);

  useEffect(() => {
    if (hidden || dismissed || list.length < 1) return undefined;
    setVisible(true);
    const hideTimer = setTimeout(() => setVisible(false), 7000);
    const nextTimer = setTimeout(() => {
      setIndex((current) => (current + 1) % list.length);
    }, 9000);
    return () => {
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, [hidden, dismissed, list.length, index]);

  useEffect(() => {
    if (!dismissed) return undefined;
    const timer = setTimeout(() => setDismissed(false), 15 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [dismissed]);

  const closeProof = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDismissed(true);
    setVisible(false);
  };

  if (hidden || dismissed || !list.length) return null;
  const item = list[index] || list[0];
  const place = [item.name, item.city].filter(Boolean).join(' · ');
  const action = t.ordered;
  const inner = (
    <>
      <span className="shop-proof__photo">
        {item.product_image ? <img src={item.product_image} alt="" referrerPolicy="no-referrer" /> : <MarketplaceLogo />}
      </span>
      <span className="shop-proof__copy">
        <span className="shop-proof__meta">
          <strong>{place}</strong>
          {item.time_label && <small>{item.time_label}</small>}
        </span>
        <em>{action} {item.product_title}</em>
        {item.message && <q dir="auto">{item.message}</q>}
      </span>
    </>
  );

  return (
    <div className={`shop-proof${visible ? ' is-on' : ''}`} role="status" aria-live="polite">
      <div className="shop-proof__card">
        <button type="button" className="shop-proof__close" onClick={closeProof} aria-label={raw.close}>
          ×
        </button>
        {item.product_slug ? (
          <Link to={`/marketplace/p/${item.product_slug}`} className="shop-proof__link">
            {inner}
          </Link>
        ) : (
          <div className="shop-proof__link">{inner}</div>
        )}
      </div>
    </div>
  );
}

function SaveToast() {
  const { t, raw } = useShopI18n();
  const [product, setProduct] = useState(null);
  const [visible, setVisible] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const onSave = (event) => {
      setProduct(event.detail?.product || null);
      setVisible(true);
      setAdding(false);
      setName('');
      setNote('');
    };
    window.addEventListener('taa-shop-save-toast', onSave);
    return () => window.removeEventListener('taa-shop-save-toast', onSave);
  }, []);

  useEffect(() => {
    if (!visible || adding) return undefined;
    const timer = setTimeout(() => setVisible(false), 5200);
    return () => clearTimeout(timer);
  }, [visible, adding, product]);

  const createCollection = async (event) => {
    event.preventDefault();
    const label = name.trim();
    if (label.length < 2 || !product?.slug) return;
    try {
      if (isShopLoggedIn()) {
        await createShopCollection(label, product.slug);
      } else {
        addLocalCollection(label, product.slug);
      }
      setNote(t.collectionCreated);
      setAdding(false);
      setTimeout(() => setVisible(false), 1400);
    } catch {
      setNote(t.collectionFail);
    }
  };

  if (!product) return null;

  return (
    <div className={`shop-save-toast${visible ? ' is-on' : ''}`} role="status" aria-live="polite">
      <div className="shop-save-toast__row">
        <span className="shop-save-toast__photo">
          {product.image_url ? <img src={product.image_url} alt="" referrerPolicy="no-referrer" /> : <MarketplaceLogo />}
        </span>
        <span className="shop-save-toast__copy">
          <strong>{product.title}</strong>
          <em>{t.savedLabel}</em>
        </span>
        <button type="button" className="shop-save-toast__close" onClick={() => setVisible(false)} aria-label={raw.close}>
          ×
        </button>
      </div>
      {adding ? (
        <form className="shop-save-toast__form" onSubmit={createCollection}>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={raw.collectionName}
            aria-label={raw.collectionName}
            autoFocus
          />
          <button type="submit">{t.create}</button>
        </form>
      ) : (
        <button type="button" className="shop-save-toast__add" onClick={() => setAdding(true)}>
          {t.addCollection}
        </button>
      )}
      {note ? <p className="shop-save-toast__note">{note}</p> : null}
    </div>
  );
}

function FollowToast() {
  const { t, raw } = useShopI18n();
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    const onFollow = (event) => {
      setName(event.detail?.name || '');
      setVisible(true);
    };
    window.addEventListener('taa-shop-follow-toast', onFollow);
    return () => window.removeEventListener('taa-shop-follow-toast', onFollow);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(() => setVisible(false), 4800);
    return () => clearTimeout(timer);
  }, [visible, name]);

  return (
    <div className={`shop-follow-toast${visible ? ' is-on' : ''}`} role="status" aria-live="polite">
      <p>
        <strong>{name ? t.followToastTitle(name) : t.followToastTitleGeneric}</strong>
        <span>{t.followToastText}</span>
      </p>
      <button type="button" onClick={() => setVisible(false)} aria-label={raw.close}>
        ×
      </button>
    </div>
  );
}

function MarketplaceShell({ children, search, onSearch, onSearchSubmit, cartCount, savedCount, showSearch, home, product, hideProof, socialProof }) {
  const { t, raw, isAr } = useShopI18n();
  const location = useLocation();
  const mainRef = useRef(null);
  const [customer, setCustomer] = useState(() => getShopCustomer());

  useEffect(() => {
    const sync = () => setCustomer(getShopCustomer());
    window.addEventListener('taa-shop-auth', sync);
    return () => window.removeEventListener('taa-shop-auth', sync);
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (main) main.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return (
    <div className={`shop-app${isAr ? ' shop-app--ar' : ''}`}>
      <SocialProofToasts items={socialProof} hidden={hideProof} />
      <SaveToast />
      <FollowToast />
      <div className="shop-app__stage">
        <div
          ref={mainRef}
          className={`shop-app__main ${home ? 'shop-app__main--home' : ''} ${product ? 'shop-app__main--product' : ''}`.trim()}
          dir={isAr ? 'rtl' : 'ltr'}
        >
        {home && <LivraisonTicker />}
        {children}
        {showSearch && (
          <form
            className="shop-app__search"
            onSubmit={(e) => {
              e.preventDefault();
              onSearchSubmit?.();
            }}
          >
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={raw.searchPlaceholder}
              aria-label={raw.searchAria}
            />
            <button type="submit" aria-label={raw.searchAria}>
              <ArrowIcon />
            </button>
          </form>
        )}
        </div>
      </div>
      <aside className="shop-app__rail">
        <Link to="/marketplace" className="shop-app__logo" aria-label={raw.logoAria}>
          <span className="shop-app__logo-mark">
            <MarketplaceLogo />
          </span>
        </Link>
        <nav className="shop-app__nav">
          <NavLink to="/marketplace" end className={({ isActive }) => (isActive ? 'is-active' : '')}>
            <HomeIcon />
            <span>{t.home}</span>
          </NavLink>
          <NavLink to="/marketplace/browse" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            <GridIcon />
            <span>{t.browse}</span>
          </NavLink>
          <NavLink
            to="/marketplace/cart"
            className={({ isActive }) => (isActive ? 'is-active shop-app__nav-cart' : 'shop-app__nav-cart')}
            onPointerDown={() => prefetchCheckoutQuote(readCart()).catch(() => {})}
            onMouseEnter={() => prefetchCheckoutQuote(readCart()).catch(() => {})}
            onFocus={() => prefetchCheckoutQuote(readCart()).catch(() => {})}
          >
            <CartIcon />
            {cartCount > 0 && <span className="shop-app__badge">{cartCount}</span>}
            <span>{t.cart}</span>
          </NavLink>
          <NavLink to="/marketplace/deals" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            <TagIcon />
            <span>{t.deals}</span>
          </NavLink>
          <NavLink to="/marketplace/saved" className={({ isActive }) => (isActive ? 'is-active shop-app__nav-saved' : 'shop-app__nav-saved')}>
            <HeartIcon filled />
            {savedCount > 0 && <span className="shop-app__badge">{savedCount}</span>}
            <span>{t.saved}</span>
          </NavLink>
        </nav>
        <div className="shop-app__rail-end">
          <Link to="/marketplace/account" className="shop-app__signin">
            <ProfileIcon />
            <span>{customer ? t.account : t.signIn}</span>
          </Link>
        </div>
      </aside>
    </div>
  );
}

const FLOAT_SLOTS = [
  { top: '10%', left: 'calc(50% - 70px)', kind: 'photo', rotate: -3 },
  { top: '16%', left: '8%', kind: 'card', rotate: -7 },
  { top: '12%', right: '8%', kind: 'photo', rotate: 5 },
  { top: '36%', left: '12%', kind: 'photo', rotate: 4 },
  { top: '32%', right: '10%', kind: 'card', rotate: 6 },
  { bottom: '14%', left: '20%', kind: 'card', rotate: -4 },
  { bottom: '12%', right: '18%', kind: 'photo', rotate: 3 },
];

function discountPercent(product) {
  if (!product?.compare_at_price || Number(product.compare_at_price) <= Number(product.price)) return 0;
  return Math.round((1 - Number(product.price) / Number(product.compare_at_price)) * 100);
}

function productSocial(product) {
  const count = Number(product?.review_count || 0);
  const avg = Number(product?.rating_avg || 0);
  if (count > 0) {
    return { rating: avg, reviews: count, real: true };
  }
  return { rating: 0, reviews: 0, real: false };
}

function mostRatedProduct(products = []) {
  return [...products].sort((a, b) => {
    const reviews = Number(b.review_count || 0) - Number(a.review_count || 0);
    if (reviews) return reviews;
    return Number(b.rating_avg || 0) - Number(a.rating_avg || 0);
  })[0] || null;
}

function pinProductFirst(products, slug) {
  if (!slug) return products;
  const index = products.findIndex((item) => item.slug === slug);
  if (index <= 0) return products;
  const next = [...products];
  const [item] = next.splice(index, 1);
  next.unshift(item);
  return next;
}

function StarRow({ reviews }) {
  return (
    <span className="shop-tile__stars" aria-hidden="true">
      ★★★★★
      <span>({reviews})</span>
    </span>
  );
}

function ShopImg({ src, alt = '', eager = false, width = 360, height = 480 }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={eager ? 'high' : 'low'}
      referrerPolicy="no-referrer"
    />
  );
}

const ShopTile = memo(function ShopTile({ product, eager = false }) {
  const { t, raw } = useShopI18n();
  const off = discountPercent(product);
  const social = productSocial(product);
  const [saved, setSaved] = useState(() => readSaved().includes(product.slug));

  useEffect(() => {
    const sync = () => setSaved(readSaved().includes(product.slug));
    window.addEventListener('taa-shop-saved', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('taa-shop-saved', sync);
      window.removeEventListener('storage', sync);
    };
  }, [product.slug]);

  return (
    <Link
      to={`/marketplace/p/${product.slug}`}
      className={`shop-tile${product.is_sold_out ? ' is-sold-out' : ''}`}
      onPointerDown={() => prefetchShopProduct(product.slug)}
      onMouseEnter={() => prefetchShopProduct(product.slug)}
      onFocus={() => prefetchShopProduct(product.slug)}
    >
      <div className="shop-tile__media">
        {product.image_url ? <ShopImg src={product.image_url} eager={eager} /> : <MarketplaceLogo />}
        {product.is_sold_out && <span className="shop-tile__badge shop-tile__badge--sold">{t.soldOut}</span>}
        {!product.is_sold_out && off > 0 && <span className="shop-tile__badge">{t.percentOff(off)}</span>}
        {!product.is_sold_out && off <= 0 && product.is_highlighted && <span className="shop-tile__badge shop-tile__badge--highlight">{t.featured}</span>}
        {!product.is_sold_out && off <= 0 && !product.is_highlighted && product.has_free_delivery && (
          <span className="shop-tile__badge shop-tile__badge--delivery">{t.freeDelivery}</span>
        )}
        <button
          type="button"
          className={`shop-tile__heart${saved ? ' is-saved' : ''}`}
          aria-label={saved ? raw.unsave : raw.save}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setSaved(saveOrUnsave(product));
          }}
        >
          <HeartIcon filled={saved} />
        </button>
      </div>
      <p className="shop-tile__brand">{product.cooperative_name || product.category_name || 'taaouniaty'}</p>
      <h3>{product.title}</h3>
      <StarRow reviews={social.reviews} />
      <p className="shop-tile__price">
        {formatPrice(product)}
        {off > 0 && <s>{Number(product.compare_at_price).toFixed(2)} {product.currency || 'MAD'}</s>}
      </p>
    </Link>
  );
});

function ProductRail({ title, to, products, icon, eagerCount = 0 }) {
  const { t, raw } = useShopI18n();
  const scrollerRef = useRef(null);
  const scrollRail = (direction) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const tile = scroller.querySelector('.shop-tile');
    const step = tile ? tile.getBoundingClientRect().width + 12 : scroller.clientWidth / 6;
    scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
  };
  if (!products?.length) return null;
  const head = to ? (
    <Link to={to} className="shop-rail__head">
      {icon}
      {title}
      <span aria-hidden="true">›</span>
    </Link>
  ) : (
    <h2 className="shop-rail__head">
      {icon}
      {title}
    </h2>
  );
  return (
    <section className="shop-rail">
      {head}
      <div className="shop-rail__frame">
        <div
          className={`shop-rail__scroller${products.length < 6 ? ' is-short' : ''}`}
          ref={scrollerRef}
        >
          {products.map((product, index) => (
            <ShopTile key={product.id || product.slug} product={product} eager={index < eagerCount} />
          ))}
        </div>
        {products.length >= 6 && (
          <>
            <button type="button" className="shop-rail__prev" aria-label={raw.prevAria(typeof title === 'string' ? title : '')} onClick={() => scrollRail(-1)}>
              ‹
            </button>
            <button type="button" className="shop-rail__next" aria-label={raw.nextAria(typeof title === 'string' ? title : '')} onClick={() => scrollRail(1)}>
              ›
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function LivraisonTicker() {
  const { t, raw } = useShopI18n();
  const line = raw.ticker;
  return (
    <div className="shop-ticker" role="note">
      <div className="shop-ticker__track">
        <span>{line.repeat(4)}</span>
        <span>{line.repeat(4)}</span>
      </div>
    </div>
  );
}

function ProductCard({ product, className = '', style, eager = false }) {
  const { t } = useShopI18n();
  return (
    <Link
      to={`/marketplace/p/${product.slug}`}
      className={`shop-product${product.is_sold_out ? ' is-sold-out' : ''} ${className}`.trim()}
      style={style}
      onPointerDown={() => prefetchShopProduct(product.slug)}
      onMouseEnter={() => prefetchShopProduct(product.slug)}
      onFocus={() => prefetchShopProduct(product.slug)}
    >
      <div className="shop-product__media">
        {product.image_url ? <ShopImg src={product.image_url} eager={eager} width={280} height={360} /> : <MarketplaceLogo />}
        {product.is_sold_out && <span className="shop-product__sold">{t.soldOut}</span>}
      </div>
      <h3>{product.title}</h3>
      <p>
        {formatPrice(product)}
        {product.compare_at_price && Number(product.compare_at_price) > Number(product.price) && (
          <s>{Number(product.compare_at_price).toFixed(2)} {product.currency || 'MAD'}</s>
        )}
      </p>
    </Link>
  );
}

function HomeSearch({ catalog, stories }) {
  const { t, raw } = useShopI18n();
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const cooperatives = useMemo(() => collectCooperatives(catalog, stories, raw.kindCoop), [catalog, stories, raw.kindCoop]);
  const suggestions = useMemo(
    () => buildHomeSuggestions(catalog, cooperatives, query, { category: raw.kindCategory, product: raw.kindProduct }),
    [catalog, cooperatives, query, raw.kindCategory, raw.kindProduct]
  );

  useEffect(() => {
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const openItem = (item) => {
    if (!item) return;
    setOpen(false);
    if (item.type === 'product') {
      navigate(`/marketplace/p/${item.slug}`);
      return;
    }
    if (item.type === 'category') {
      navigate(browseCategoryPath(item.slug));
      return;
    }
    const hasProducts = (catalog.products || []).some(
      (product) => product.cooperative_slug === item.slug || product.cooperative_name === item.name
    );
    if (hasProducts) {
      navigate(coopShopPath(item.slug));
      return;
    }
    if (item.storySlug) {
      navigate(articleCoopPath(item.storySlug));
      return;
    }
    navigate(`/marketplace/browse?q=${encodeURIComponent(item.name)}`);
  };

  const submit = () => {
    if (open && suggestions[active]) {
      openItem(suggestions[active]);
      return;
    }
    const q = query.trim();
    if (!q) return;
    navigate(`/marketplace/browse?q=${encodeURIComponent(q)}`);
  };

  return (
    <form
      ref={boxRef}
      className="shop-home__search"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (!open || !suggestions.length) return;
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((current) => (current + 1) % suggestions.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((current) => (current - 1 + suggestions.length) % suggestions.length);
          } else if (event.key === 'Enter' && suggestions[active]) {
            event.preventDefault();
            openItem(suggestions[active]);
          }
        }}
        placeholder={raw.homeSearchPlaceholder}
        aria-label={raw.homeSearchAria}
        aria-autocomplete="list"
        autoComplete="off"
      />
      <button type="submit" aria-label={raw.searchAria}>
        <SearchIcon />
      </button>
      {open && suggestions.length > 0 && (
        <ul className="shop-home__suggest" role="listbox">
          {suggestions.map((item, index) => (
            <li key={item.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={`shop-home__suggest-item ${index === active ? 'is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => openItem(item)}
              >
                <span className="shop-home__suggest-copy">{item.name}</span>
                <span className="shop-home__suggest-kind">{item.type === 'category' ? t.kindCategory : item.type === 'coop' ? t.kindCoop : t.kindProduct}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}

function ShopNetwork({ stories = [] }) {
  const { t } = useShopI18n();
  return (
    <section className="shop-network" aria-labelledby="shop-network-headline">
      <div className="shop-network__copy">
        <p className="shop-network__kicker">{t.networkKicker}</p>
        <h1 id="shop-network-headline">{t.homeHeadline}</h1>
        <p>{t.homePitch}</p>
        <ul className="shop-network__why">
          <li>{t.homeWhyBuy}</li>
          <li>{t.homeWhyShip}</li>
          <li>{t.homeWhyPay}</li>
        </ul>
        <div className="shop-network__faq">
          <h3>{t.homeFaqTitle}</h3>
          {(t.homeFaq || []).map((item, index) => (
            <details key={index}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
      <div className="shop-network__aside" aria-hidden="true">
        <img className="shop-network__watermark" src={taaounityLogo} alt="" />
        <div className="shop-network__faces">
          {stories
            .map((story) => story.cover_url || story.image_url)
            .filter(Boolean)
            .slice(0, 3)
            .map((src, index) => (
              <img key={`${src}-${index}`} src={src} alt="" />
            ))}
        </div>
      </div>
    </section>
  );
}

function ShopFooter() {
  const { t, raw, language } = useShopI18n();
  const marketplaceUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/marketplace`
      : buildAbsoluteUrl('/marketplace');

  return (
    <footer className="shop-foot">
      <div className="shop-foot__top">
        <div className="shop-foot__brand">
          <div className="shop-foot__qr-block">
            <Link to="/marketplace" className="shop-foot__lockup">
              <MarketplaceLogo />
              <span className="shop-foot__titles">
                <strong>taaouniaty</strong>
                <small>{t.marketplace}</small>
              </span>
            </Link>
            <p>
              {t.footLine1}
              <br />
              {t.footLine2}
            </p>
            <div className="shop-foot__qr">
              <QRCodeSVG
                value={marketplaceUrl}
                size={168}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#000000"
                title={raw.qrTitle}
                imageSettings={{
                  src: taaounityLogo,
                  height: 56,
                  width: 56,
                  excavate: true,
                }}
              />
              <span className="shop-foot__qr-brand">
                <MarketplaceLogo />
                <em>
                  <strong>taaouniaty</strong>
                  <small>{t.marketplace}</small>
                </em>
              </span>
            </div>
          </div>
        </div>
        <div className="shop-foot__cols">
          <div>
            <h3>{t.discover}</h3>
            <Link to="/marketplace">{t.home}</Link>
            <Link to="/marketplace/browse">{t.browse}</Link>
            <Link to="/marketplace/deals">{t.deals}</Link>
            <Link to={ARTICLE_COOP_LIST}>{t.cooperatives}</Link>
          </div>
          <div>
            <h3>{t.sell}</h3>
            <Link to="/register">{t.joinNetwork}</Link>
            <Link to="/login">{t.signIn}</Link>
            <Link to="/register">{t.createShop}</Link>
          </div>
          <div>
            <h3>{t.social}</h3>
            <a href="https://www.instagram.com/taaouniaty/" target="_blank" rel="noreferrer">Instagram</a>
            <Link to={ARTICLE_BLOG_LIST}>{t.blog}</Link>
          </div>
          <div>
            <h3>{t.brandCol}</h3>
            <Link to="/">{t.theSite}</Link>
            <Link to="/marketplace">{t.marketplace}</Link>
            <Link to="/article/cooperatives">{t.portraitsLink}</Link>
          </div>
        </div>
      </div>
      <PreferredSourceButton language={language} compact />
      <div className="shop-foot__bottom">
        <p className="shop-foot__powered">
          {t.poweredBy} <strong>taaouniaty</strong>
          <span> · </span>
          <Link to="/register">{t.joinNetwork}</Link>
        </p>
        <p className="shop-foot__copy">© Taaouniaty {new Date().getFullYear()}</p>
      </div>
    </footer>
  );
}

function ExploreView({ catalog, stories }) {
  const { t } = useShopI18n();
  const products = (catalog.products || []).filter((p) => p.image_url);
  const allProducts = catalog.products || [];
  const categories = catalog.categories || [];
  const deals = (catalog.deals || []).slice(0, 4);
  const [followedSlugs, setFollowedSlugs] = useState(() => (isShopLoggedIn() ? readFollowedCoops() : []));
  const slides = useMemo(() => {
    const source = products.length ? products : [];
    if (!source.length) return [];
    return [0, 1, 2].map((slide) =>
      FLOAT_SLOTS.map((_, i) => source[(slide * FLOAT_SLOTS.length + i) % source.length])
    );
  }, [products]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const loadFollows = () => {
      if (!isShopLoggedIn()) {
        setFollowedSlugs([]);
        return;
      }
      fetchShopFollows()
        .then((data) => {
          const slugs = (Array.isArray(data) ? data : []).map((item) => item.slug).filter(Boolean);
          writeFollowedCoops(slugs);
          setFollowedSlugs(slugs);
        })
        .catch(() => setFollowedSlugs(readFollowedCoops()));
    };
    loadFollows();
    window.addEventListener('taa-shop-auth', loadFollows);
    window.addEventListener('taa-shop-follows', loadFollows);
    return () => {
      window.removeEventListener('taa-shop-auth', loadFollows);
      window.removeEventListener('taa-shop-follows', loadFollows);
    };
  }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setSlide((current) => (current + 1) % slides.length);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const items = slides[slide] || [];

  const renderFloat = (product, index) => {
    const slot = FLOAT_SLOTS[index];
    const style = {
      animationDelay: `${index * 0.2}s`,
      '--rot': `${slot.rotate}deg`,
    };
    if (slot.top) style.top = slot.top;
    if (slot.left) style.left = slot.left;
    if (slot.right) style.right = slot.right;
    if (slot.bottom) style.bottom = slot.bottom;
    const layer = 'shop-home__float--back';
    if (slot.kind === 'card') {
      return (
        <ProductCard
          key={`${slide}-${product.id}-${index}`}
          product={product}
          className={`shop-home__float ${layer}`}
          style={style}
          eager={index < 3}
        />
      );
    }
    return (
      <Link
        key={`${slide}-${product.id}-${index}`}
        to={`/marketplace/p/${product.slug}`}
        className={`shop-home__float shop-float-photo ${layer}`}
        style={style}
      >
        <img src={product.image_url} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" fetchPriority={index < 2 ? 'high' : 'low'} referrerPolicy="no-referrer" />
      </Link>
    );
  };

  const featured = useMemo(() => {
    const skip = new Set(deals.map((deal) => deal.product_slug).filter(Boolean));
    const preferred = allProducts.filter((product) => product.is_featured && !skip.has(product.slug));
    const rest = allProducts.filter((product) => !preferred.includes(product) && !skip.has(product.slug));
    return [...preferred, ...rest].slice(0, 6);
  }, [allProducts, deals]);
  const followedProducts = useMemo(() => {
    if (!followedSlugs.length) return [];
    const followed = new Set(followedSlugs);
    return sortBrowseProducts(
      allProducts.filter((product) => product.cooperative_slug && followed.has(product.cooperative_slug)),
      'new'
    ).slice(0, 12);
  }, [allProducts, followedSlugs]);
  const spotlight = useMemo(() => {
    const product =
      allProducts.find((item) => item.is_featured && item.cooperative_name) ||
      allProducts.find((item) => item.cooperative_name);
    if (!product) return null;
    const coop = (catalog.cooperatives || []).find((item) => item.slug === product.cooperative_slug);
    const storyEntry = (stories || []).find(
      (item) =>
        (item.slug && item.slug === product.cooperative_slug) ||
        (item.cooperative_name && item.cooperative_name === product.cooperative_name) ||
        (item.title && item.title === product.cooperative_name)
    );
    return {
      product,
      name: product.cooperative_name,
      slug: product.cooperative_slug,
      region: coop?.region || storyEntry?.region || '',
      story:
        coop?.story ||
        storyEntry?.tagline ||
        storyEntry?.excerpt ||
        t.spotlightStory(product.title, product.cooperative_name),
      profileTo: storyEntry?.slug ? articleCoopPath(storyEntry.slug) : ARTICLE_COOP_LIST,
    };
  }, [allProducts, catalog.cooperatives, stories, t]);

  return (
    <div className="shop-home">
      <div className="shop-home__stage">
        {items.map((product, index) => renderFloat(product, index))}
        <p className="shop-home__brand">
          taaouniaty
          <span>Marketplace</span>
        </p>
      </div>

      <HomeSearch catalog={catalog} stories={stories} />

      <div className="shop-home__cats">
        {categories.map((cat) => {
          const thumb =
            cat.image_url ||
            products.find((p) => p.category_slug === cat.slug)?.image_url;
          return (
            <Link
              key={cat.id}
              to={browseCategoryPath(cat.slug)}
              className="shop-home__cat"
            >
              {thumb ? <img src={thumb} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" /> : <span className="shop-home__cat-dot" style={{ background: cat.color }} />}
              {cat.name}
            </Link>
          );
        })}
      </div>

      <div className="shop-home__below">
        {followedProducts.length > 0 && (
          <ProductRail
            title={t.followedRail}
            eagerCount={4}
            products={followedProducts}
          />
        )}
        {deals.length > 0 && (
          <ProductRail
            title={t.ofTheMoment}
            to="/marketplace/deals"
            eagerCount={4}
            products={deals
              .map((deal) => deal.product_detail || allProducts.find((item) => item.slug === deal.product_slug))
              .filter(Boolean)}
          />
        )}

        <ProductRail title={t.featuredRail} to="/marketplace/browse" products={featured} eagerCount={deals.length ? 0 : 4} />

        {categories
          .filter((cat) => allProducts.some((product) => product.category_slug === cat.slug))
          .map((cat) => (
          <ProductRail
            key={`rail-${cat.slug}`}
            title={cat.name}
            to={browseCategoryPath(cat.slug)}
            products={allProducts.filter((product) => product.category_slug === cat.slug).slice(0, 8)}
          />
        ))}

        {spotlight && (
          <ProductRail
            title={t.atCoop(spotlight.name)}
            to={spotlight.profileTo}
            products={allProducts.filter((product) => product.cooperative_slug === spotlight.slug).slice(0, 8)}
          />
        )}

        <ShopNetwork stories={stories} />
        <ShopFooter />
      </div>
    </div>
  );
}

function SavedView({ catalog }) {
  const { t, raw, language } = useShopI18n();
  const [slugs, setSlugs] = useState(readSaved);
  const products = catalog.products || [];

  useEffect(() => {
    const sync = () => setSlugs(readSaved());
    window.addEventListener('taa-shop-saved', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('taa-shop-saved', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const list = slugs
    .map((slug) => products.find((product) => product.slug === slug))
    .filter(Boolean);

  return (
    <>
      <SeoHead noindex title={raw.savedSeoTitle} description="" language={language} />
      <div className="shop-browse">
        <div className="shop-browse__head">
          <h1 className="shop-saved__title">
            <HeartIcon filled />
            {t.saved}
          </h1>
        </div>

        {list.length ? (
          <div className="shop-pdp__liked-grid">
            {list.map((product) => (
              <ShopTile key={product.id || product.slug} product={product} />
            ))}
          </div>
        ) : (
          <div className="shop-browse__empty">
            <p>{t.savedEmpty}</p>
            <Link to="/marketplace/browse">{t.browseProducts}</Link>
          </div>
        )}
      </div>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function BrowseView({ catalog, query }) {
  const { t, raw, language } = useShopI18n();
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const legacyCategory = params.get('category') || '';
  const selected = categorySlug || '';
  const coop = params.get('coop') || '';
  const sort = params.get('sort') || 'new';
  const products = catalog.products || [];
  const categories = catalog.categories || [];
  const category = categories.find((item) => item.slug === selected) || null;
  const q = (params.get('q') || query || '').trim().toLowerCase();

  useEffect(() => {
    if (!legacyCategory || categorySlug) return;
    const next = new URLSearchParams(params);
    next.delete('category');
    const qs = next.toString();
    navigate(`${browseCategoryPath(legacyCategory)}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [legacyCategory, categorySlug, navigate, params]);

  const list = sortBrowseProducts(
    products.filter((p) => {
      const matchCat = !selected || p.category_slug === selected;
      const matchCoop =
        !coop ||
        p.cooperative_slug === coop ||
        (p.cooperative_name || '').toLowerCase() === coop.toLowerCase();
      const haystack = `${p.title} ${p.description || ''} ${p.category_name || ''} ${p.cooperative_name || ''}`;
      const matchQ = !q || haystack.toLowerCase().includes(q);
      return matchCat && matchCoop && matchQ;
    }),
    sort
  );

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    if (value === 'new') next.delete('sort');
    else next.set('sort', value);
    next.delete('category');
    setParams(next, { replace: true });
  };

  const pagePath = browseCategoryPath(selected);
  const seo = category
    ? {
        title: raw.browseTitle(category.name),
        description: categoryBlurb(category, raw),
        keywords: `${category.name}, ${MARKETPLACE_BROWSE_SEO.keywords}`,
      }
    : { ...MARKETPLACE_BROWSE_SEO, title: raw.allProducts };

  return (
    <>
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        url={buildAbsoluteUrl(pagePath)}
        jsonLd={buildMarketplaceBrowseJsonLd({ products: list, category, path: pagePath })}
        language={language}
      />
      <div className="shop-browse">
        <div className="shop-browse__head">
          <h1>{category ? category.name : t.allProducts}</h1>
          <label className="shop-browse__sort">
            <span>{t.sort}</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="new">{raw.sortNew}</option>
              <option value="price-asc">{raw.sortPriceAsc}</option>
              <option value="price-desc">{raw.sortPriceDesc}</option>
            </select>
          </label>
        </div>

        <div className="shop-chips">
          <Link to="/marketplace/browse" className={!selected ? 'is-active' : ''}>{t.all}</Link>
          {categories.map((cat) => (
            <Link
              key={cat.id || cat.slug}
              to={browseCategoryPath(cat.slug)}
              className={selected === cat.slug ? 'is-active' : ''}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {category && <p className="shop-browse__blurb">{categoryBlurb(category, t)}</p>}

        {list.length ? (
          <div className="shop-browse__rails">
            {(selected
              ? [category || { slug: selected, name: category?.name || selected }]
              : [
                  ...categories,
                  { slug: '', name: t.others },
                ]
            )
              .map((cat) => ({
                cat,
                items: sortBrowseProducts(
                  list.filter((product) =>
                    cat.slug ? product.category_slug === cat.slug : !product.category_slug
                  ),
                  sort
                ),
              }))
              .filter((row) => row.items.length)
              .map(({ cat, items }) => (
                <ProductRail
                  key={cat.slug || 'autres'}
                  title={cat.name}
                  to={browseCategoryPath(cat.slug)}
                  products={items}
                />
              ))}
          </div>
        ) : (
          <div className="shop-browse__empty">
            <p>{t.emptyProducts}</p>
            <Link to="/marketplace/browse">{t.allProducts}</Link>
          </div>
        )}

        <p className="shop-browse__coop">
          {t.coopPrompt}{' '}
          <Link to={ARTICLE_COOP_LIST}>{t.coopPromptLink}</Link>
        </p>
      </div>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function DealsView({ catalog, query }) {
  const { t, raw, language } = useShopI18n();
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const selected = categorySlug || '';
  const sort = params.get('sort') || 'new';
  const deals = catalog.deals || [];
  const products = catalog.products || [];
  const categories = catalog.categories || [];
  const category = categories.find((item) => item.slug === selected) || null;
  const q = (params.get('q') || query || '').trim().toLowerCase();

  const promoProducts = products.filter((product) => productHasPromo(product, deals));
  const list = sortBrowseProducts(
    promoProducts.filter((product) => {
      const matchCat = !selected || product.category_slug === selected;
      const haystack = `${product.title} ${product.description || ''} ${product.category_name || ''} ${product.cooperative_name || ''}`;
      return !q || haystack.toLowerCase().includes(q);
    }),
    sort
  );
  const chips = categories.filter((cat) =>
    promoProducts.some((product) => product.category_slug === cat.slug)
  );

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    if (value === 'new') next.delete('sort');
    else next.set('sort', value);
    setParams(next, { replace: true });
  };

  const pagePath = dealsCategoryPath(selected);

  return (
    <>
      <SeoHead
        title={category ? raw.dealsSeoTitleCat(category.name) : raw.dealsSeoTitle}
        description={category ? raw.dealsSeoDescCat(category.name) : raw.dealsSeoDesc}
        url={buildAbsoluteUrl(pagePath)}
        language={language}
      />
      <div className="shop-browse">
        <div className="shop-browse__head">
          <h1>{category ? category.name : t.deals}</h1>
          <label className="shop-browse__sort">
            <span>{t.sort}</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="new">{raw.sortNew}</option>
              <option value="price-asc">{raw.sortPriceAsc}</option>
              <option value="price-desc">{raw.sortPriceDesc}</option>
            </select>
          </label>
        </div>

        <div className="shop-chips">
          <Link to="/marketplace/deals" className={!selected ? 'is-active' : ''}>{t.all}</Link>
          {chips.map((cat) => (
            <Link
              key={cat.id || cat.slug}
              to={dealsCategoryPath(cat.slug)}
              className={selected === cat.slug ? 'is-active' : ''}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {category && (
          <p className="shop-browse__blurb">{t.dealsBlurb(category.name)}</p>
        )}

        {list.length ? (
          <div className="shop-browse__rails">
            {(selected
              ? [category || { slug: selected, name: selected }]
              : [...chips, { slug: '', name: t.others }]
            )
              .map((cat) => ({
                cat,
                items: sortBrowseProducts(
                  list.filter((product) =>
                    cat.slug ? product.category_slug === cat.slug : !product.category_slug
                  ),
                  sort
                ),
              }))
              .filter((row) => row.items.length)
              .map(({ cat, items }) => (
                <ProductRail
                  key={cat.slug || 'autres'}
                  title={cat.name}
                  to={dealsCategoryPath(cat.slug)}
                  products={items}
                />
              ))}
          </div>
        ) : (
          <div className="shop-browse__empty">
            <p>{t.dealsEmpty}</p>
            <Link to="/marketplace/deals">{t.allDeals}</Link>
          </div>
        )}

        <p className="shop-browse__coop">
          {t.coopPrompt}{' '}
          <Link to={ARTICLE_COOP_LIST}>{t.coopPromptLink}</Link>
        </p>
      </div>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function ProductGrid({ products, empty }) {
  if (!products.length) return <p className="shop-empty">{empty}</p>;
  return (
    <div className="shop-products">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}

function productReviews(product) {
  const seed = String(product.id || product.slug || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const names = ['Amina', 'Youssef', 'Fatima', 'Karim'];
  const texts = [
    'Très beau travail.',
    'Bien reçu, je recommande.',
    'Qualité au rendez-vous.',
    'Parfait pour offrir.',
  ];
  const tones = ['#f3c6a8', '#d7c4b0', '#e8b89a', '#cfc3b6'];
  return [0, 1].map((index) => ({
    name: names[(seed + index) % names.length],
    text: texts[(seed + index * 2) % texts.length],
    ago: index === 0 ? `${2 + (seed % 5)} jours` : '21 mai 2026',
    tone: tones[(seed + index) % tones.length],
  }));
}

function videoPosterUrl(url) {
  const raw = String(url || '');
  if (raw.includes('/video/upload/')) {
    return raw.replace('/video/upload/', '/video/upload/so_0,f_jpg,q_auto/');
  }
  return raw;
}

function productGallery(product) {
  const items = [];
  const seen = new Set();
  const push = (item) => {
    if (!item) return;
    const url = typeof item === 'string' ? item : (item.url || item.src || '');
    if (!url || seen.has(url)) return;
    seen.add(url);
    items.push({
      url,
      type: (item && item.type === 'video') || String(url).includes('/video/upload/') ? 'video' : 'image',
    });
  };
  (product.gallery || []).forEach(push);
  if (!items.length) {
    push(product.image_url);
    (product.images || []).forEach(push);
  }
  return items;
}

function findCoopStory(product, stories = []) {
  const slug = (product.cooperative_slug || '').toLowerCase();
  const name = (product.cooperative_name || '').toLowerCase();
  if (!slug && !name) return null;
  return (
    stories.find((item) => {
      const itemSlug = (item.slug || '').toLowerCase();
      const catalogSlug = (item.catalog_slug || '').toLowerCase();
      const itemName = (item.cooperative_name || item.title || '').toLowerCase();
      return (slug && (itemSlug === slug || catalogSlug === slug)) || (name && itemName === name);
    }) || null
  );
}

function isMarketplaceCoopOwner(product) {
  if (!product) return false;
  const name = (product.cooperative_name || '').trim().toLowerCase();
  if (name === 'taaouniaty') return false;
  return Boolean(product.cooperative || product.cooperative_slug);
}

function CoopOwnerCard({ product, catalog, story }) {
  const { t, raw } = useShopI18n();
  if (!isMarketplaceCoopOwner(product)) return null;

  const slug = product.cooperative_slug || '';
  const name = (product.cooperative_name || '').trim() || slug;
  const fromCatalog = (catalog?.cooperatives || []).find((item) => (
    (slug && item.slug === slug) || (product.cooperative && item.id === product.cooperative)
  ));
  const logo = product.cooperative_logo_url || fromCatalog?.image_url || story?.logo_url || '';
  const banner = product.cooperative_banner_url || fromCatalog?.banner_url || story?.hero_image_url || story?.cover_url || '';
  const mosaic = (catalog?.products || [])
    .filter((item) => item?.image_url && item.slug !== product.slug && (
      (slug && item.cooperative_slug === slug) ||
      (product.cooperative && item.cooperative === product.cooperative)
    ))
    .slice(0, 3)
    .map((item) => item.image_url);
  if (!mosaic.length && product.image_url) mosaic.push(product.image_url);
  const coopProducts = (catalog?.products || []).filter((item) => (
    (slug && item.cooperative_slug === slug) ||
    (product.cooperative && item.cooperative === product.cooperative)
  ));
  const social = coopShopSocial(coopProducts.length ? coopProducts : [product], fromCatalog);
  const shopTo = coopShopPath(slug || story?.catalog_slug);
  const mark = (name || 'C').slice(0, 1).toUpperCase();

  return (
    <Link className="shop-seller-card" to={shopTo} aria-label={raw.coopOwnerVisit(name)}>
      <div className="shop-seller-card__banner">
        {banner ? (
          <img src={banner} alt="" referrerPolicy="no-referrer" />
        ) : (
          <div className="shop-seller-card__mosaic">
            {mosaic.length ? mosaic.map((src) => (
              <img key={src} src={src} alt="" referrerPolicy="no-referrer" />
            )) : <span>{name}</span>}
          </div>
        )}
      </div>
      <div className="shop-seller-card__logo-wrap">
        {logo ? (
          <img className="shop-seller-card__logo" src={logo} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span className="shop-seller-card__logo shop-seller-card__logo--mark" aria-hidden="true">{mark}</span>
        )}
      </div>
      <div className="shop-seller-card__body">
        <div className="shop-seller-card__meta">
          <span className="shop-seller-card__badge">{t.coopOwnerBadge}</span>
          <strong>{name}</strong>
          <p>
            <span aria-hidden="true">★</span>
            {social.reviews
              ? `${social.rating.toFixed(1)} (${social.reviews})`
              : t.coopOwnerNew}
          </p>
        </div>
        <span className="shop-seller-card__follow">{t.coopOwnerFollow}</span>
      </div>
    </Link>
  );
}

function relatedForProduct(product, catalog, apiRelated = []) {
  const pool = catalog?.products || [];
  const same = pool.filter((item) => {
    if (!item?.slug || item.slug === product.slug) return false;
    const sameCat = product.category_slug && item.category_slug === product.category_slug;
    const sameCoop =
      (product.cooperative_slug && item.cooperative_slug === product.cooperative_slug) ||
      (product.cooperative_name && item.cooperative_name === product.cooperative_name);
    return sameCat || sameCoop;
  });
  const seen = new Set();
  return [...same, ...(apiRelated || [])].filter((item) => {
    if (!item?.slug || item.slug === product.slug || seen.has(item.slug)) return false;
    seen.add(item.slug);
    return true;
  }).slice(0, 12);
}

function HeartIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        d="M12 20s-7-4.4-7-9.2C5 8 6.8 6.4 9 6.4c1.3 0 2.4.6 3 1.6.6-1 1.7-1.6 3-1.6 2.2 0 4 1.6 4 4.4 0 4.8-7 9.2-7 9.2Z"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 14V4m0 0 4 4M12 4 8 8M5 14v4.2A1.8 1.8 0 0 0 6.8 20h10.4A1.8 1.8 0 0 0 19 18.2V14"
      />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        d="M10 14a5 5 0 0 0 7.1 0l1.4-1.4a5 5 0 0 0-7.1-7.1L10 6.9M14 10a5 5 0 0 0-7.1 0L5.5 11.4a5 5 0 0 0 7.1 7.1L14 17.1"
      />
    </svg>
  );
}

function formatSupportPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('212') && digits.length >= 12) {
    return `+212 ${digits.slice(3, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }
  return raw || '';
}

function ProductMoreMenu({ product, ownerName }) {
  const { t, raw } = useShopI18n();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(null);
  const customer = getShopCustomer();
  const [form, setForm] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const supportPhone = product.support_phone || '';
  const telHref = supportPhone ? `tel:${String(supportPhone).replace(/\s/g, '')}` : '';

  useEffect(() => {
    const close = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const openSheet = (kind) => {
    setOpen(false);
    setSheet(kind);
    setDone('');
    setError('');
    setForm({
      name: getShopCustomer()?.name || '',
      phone: getShopCustomer()?.phone || '',
      message: '',
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const path = sheet === 'report' ? 'report' : 'contact';
      await axios.post(`/api/shop/products/${product.slug}/${path}/`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setDone(sheet === 'report' ? t.reportSent : t.contactSent);
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.name || data.phone || data.message || data.detail || t.ticketFail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shop-pdp__menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="shop-pdp__menu"
        aria-label={raw.moreOptions}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        ⋯
      </button>
      {open && (
        <div className="shop-pdp__menu-panel" role="menu">
          <button type="button" role="menuitem" onClick={() => openSheet('contact')}>
            {t.contactOwner(ownerName)}
          </button>
          <button type="button" role="menuitem" onClick={() => openSheet('report')}>
            {t.reportProduct}
          </button>
        </div>
      )}
      {sheet && (
        <div className="shop-sheet">
          <button type="button" className="shop-sheet__backdrop" aria-label={raw.close} onClick={() => setSheet(null)} />
          <div className="shop-sheet__panel" role="dialog" aria-modal="true">
            <button type="button" className="shop-sheet__close" onClick={() => setSheet(null)} aria-label={raw.close}>×</button>
            <h2>{sheet === 'report' ? t.reportProduct : t.contactOwner(ownerName)}</h2>
            <p>{sheet === 'report' ? t.reportHint : t.contactHint}</p>
            {done ? (
              <p className="shop-sheet__done">{done}</p>
            ) : (
              <form onSubmit={submit}>
                <label>
                  {t.name}
                  <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} required />
                </label>
                <label>
                  {t.phone}
                  <input value={form.phone} onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))} required />
                </label>
                <label>
                  {t.messageLabel}
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm((current) => ({ ...current, message: e.target.value }))}
                    required
                  />
                </label>
                {error && <p className="shop-sheet__error">{error}</p>}
                <button type="submit" className="shop-sheet__send" disabled={busy}>
                  {busy ? t.sending : t.sendMessage}
                </button>
              </form>
            )}
            {sheet === 'contact' && supportPhone && (
              <a className="shop-sheet__call" href={telHref}>
                <span>{t.contactOrCall}</span>
                <strong>{formatSupportPhone(supportPhone)}</strong>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function coopShopSocial(products = [], profile = null) {
  if (Number(profile?.review_count || 0) > 0) {
    return {
      rating: Number(profile.rating_avg || 0),
      reviews: Number(profile.review_count || 0),
      real: true,
    };
  }
  return productSocial(mostRatedProduct(products));
}

function isPdfUrl(url) {
  return /\.pdf(\?|#|$)/i.test(String(url || ''));
}

function MailGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="M4 7.2h16v10.2H4V7.2Z" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="m5 8 7 5 7-5" />
    </svg>
  );
}
function PhoneGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M7.2 4.8h3.1l1.2 3-1.7 1.2a11 11 0 0 0 5.2 5.2l1.2-1.7 3 1.2v3.1A1.7 1.7 0 0 1 17.5 19 13.2 13.2 0 0 1 5 6.5a1.7 1.7 0 0 1 2.2-1.7Z" />
    </svg>
  );
}
function PinGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="M12 21s6-5.2 6-10.2A6 6 0 0 0 6 10.8C6 15.8 12 21 12 21Z" />
      <circle cx="12" cy="10.6" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="8.2" y="8.2" width="10.2" height="11.2" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" d="M6.4 15.2V6.6A1.8 1.8 0 0 1 8.2 4.8h8.4" />
    </svg>
  );
}
function InstagramGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="16.6" cy="7.4" r="0.9" fill="currentColor" />
    </svg>
  );
}
function InfoGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M12 11.2V16M12 8.2v.2" />
    </svg>
  );
}

function CoopProfileCard({
  open,
  onClose,
  profile,
  slug,
}) {
  const { t, raw } = useShopI18n();
  const customer = getShopCustomer();
  const [reportOpen, setReportOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setReportOpen(false);
      setDone('');
      setError('');
      setCopied('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const phone = profile?.phone || '';
  const email = profile?.email || '';
  const address = profile?.address || '';
  const whatsapp = profile?.whatsapp || '';
  const instagram = profile?.instagram || '';
  const facebook = profile?.facebook || '';
  const certificates = profile?.certificates || [];
  const catalogTo = slug ? `/${slug}` : '';
  const hasContact = Boolean(phone || email || address || whatsapp || instagram || facebook);

  const copyValue = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1600);
    } catch {
      /* ignore */
    }
  };

  const openReport = () => {
    setReportOpen(true);
    setDone('');
    setError('');
    setForm({
      name: customer?.name || getShopCustomer()?.name || '',
      phone: customer?.phone || getShopCustomer()?.phone || '',
      message: '',
    });
  };

  const submitReport = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(`/api/shop/cooperatives/${slug}/report/`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setDone(t.reportSent);
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.name || data.phone || data.message || data.detail || t.ticketFail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shop-coop-card">
      <button type="button" className="shop-coop-card__backdrop" aria-label={raw.close} onClick={onClose} />
      <aside className="shop-coop-card__panel" role="dialog" aria-modal="true" aria-label={raw.coopCardContact}>
        <div className="shop-coop-card__nav">
          <button type="button" className="shop-coop-card__round" onClick={onClose} aria-label={raw.close}>
            ×
          </button>
        </div>
        <h2 className="shop-coop-card__title">{t.coopCardContact}</h2>

        <div className="shop-coop-card__group">
          {instagram ? (
            <a className="shop-coop-card__row" href={instagram} target="_blank" rel="noreferrer">
              <span>{t.coopCardInstagram}</span>
              <InstagramGlyph />
            </a>
          ) : null}
          {facebook ? (
            <a className="shop-coop-card__row" href={facebook} target="_blank" rel="noreferrer">
              <span>{t.coopCardFacebook}</span>
              <LinkIcon />
            </a>
          ) : null}
          {email ? (
            <div className="shop-coop-card__row">
              <a href={`mailto:${email}`}>{email}</a>
              <span className="shop-coop-card__row-actions">
                <a href={`mailto:${email}`} aria-label={raw.email}><MailGlyph /></a>
                <button type="button" onClick={() => copyValue(email, 'email')} aria-label={raw.coopCardCopied}>
                  <CopyGlyph />
                </button>
              </span>
            </div>
          ) : null}
          {phone ? (
            <a className="shop-coop-card__row" href={`tel:${String(phone).replace(/\s/g, '')}`}>
              <span>{formatSupportPhone(phone)}</span>
              <PhoneGlyph />
            </a>
          ) : null}
          {whatsapp ? (
            <a className="shop-coop-card__row" href={whatsapp} target="_blank" rel="noreferrer">
              <span>{t.coopCardWhatsapp}</span>
              <PhoneGlyph />
            </a>
          ) : null}
          {address ? (
            <div className="shop-coop-card__row shop-coop-card__row--address">
              <span>{address}</span>
              <PinGlyph />
            </div>
          ) : null}
          {!hasContact ? <p className="shop-coop-card__empty">{t.coopCardNoContact}</p> : null}
          {copied === 'email' ? <p className="shop-coop-card__copied">{t.coopCardCopied}</p> : null}
        </div>

        {catalogTo ? (
          <Link className="shop-coop-card__action" to={catalogTo}>
            <span>{t.coopCardVisit}</span>
            <LinkIcon />
          </Link>
        ) : null}

        {certificates.length ? (
          <div className="shop-coop-card__group">
            <p className="shop-coop-card__label">{t.coopCardCertificates}</p>
            <div className="shop-coop-card__certs">
              {certificates.map((src) => (
                <a key={src} href={src} target="_blank" rel="noreferrer" className="shop-coop-card__cert">
                  {isPdfUrl(src) ? <span>PDF</span> : <img src={src} alt="" referrerPolicy="no-referrer" />}
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <button type="button" className="shop-coop-card__action" onClick={openReport}>
          <span>{t.coopCardReport}</span>
          <InfoGlyph />
        </button>
      </aside>

      {reportOpen && (
        <div className="shop-sheet">
          <button type="button" className="shop-sheet__backdrop" aria-label={raw.close} onClick={() => setReportOpen(false)} />
          <div className="shop-sheet__panel" role="dialog" aria-modal="true">
            <button type="button" className="shop-sheet__close" onClick={() => setReportOpen(false)} aria-label={raw.close}>×</button>
            <h2>{t.coopCardReport}</h2>
            <p>{t.coopCardReportHint}</p>
            {done ? (
              <p className="shop-sheet__done">{done}</p>
            ) : (
              <form onSubmit={submitReport}>
                <label>
                  {t.name}
                  <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} required />
                </label>
                <label>
                  {t.phone}
                  <input value={form.phone} onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))} required />
                </label>
                <label>
                  {t.messageLabel}
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm((current) => ({ ...current, message: e.target.value }))}
                    required
                  />
                </label>
                {error && <p className="shop-sheet__error">{error}</p>}
                <button type="submit" className="shop-sheet__send" disabled={busy}>
                  {busy ? t.sending : t.sendMessage}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function coopPriceCap(products = []) {
  const max = Math.max(0, ...products.map((item) => Number(item.price || 0)));
  if (!max) return 2000;
  return Math.max(100, Math.ceil(max / 50) * 50);
}

function PriceRangeSlider({ min = 0, max = 2000, from, to, onChange }) {
  const span = Math.max(1, max - min);
  const start = Math.min(Math.max(min, Number(from) || min), max);
  const end = Math.min(Math.max(start, Number(to) || max), max);
  const left = ((start - min) / span) * 100;
  const right = ((end - min) / span) * 100;

  const setFrom = (value) => onChange(Math.min(Number(value) || min, end), end);
  const setTo = (value) => onChange(start, Math.max(Number(value) || max, start));

  return (
    <div className="shop-filter__slider">
      <div className="shop-filter__range">
        <span className="shop-filter__track">
          <i style={{ left: `${left}%`, width: `${Math.max(0, right - left)}%` }} />
        </span>
        <input type="range" min={min} max={max} value={start} onChange={(event) => setFrom(event.target.value)} />
        <input type="range" min={min} max={max} value={end} onChange={(event) => setTo(event.target.value)} />
      </div>
      <div className="shop-filter__bounds">
        <input
          inputMode="numeric"
          value={String(start)}
          onChange={(event) => setFrom(event.target.value.replace(/\D/g, ''))}
          aria-label="min"
        />
        <span>—</span>
        <input
          inputMode="numeric"
          value={end >= max ? `${max}+` : String(end)}
          onChange={(event) => setTo(String(event.target.value).replace(/\D/g, ''))}
          aria-label="max"
        />
      </div>
    </div>
  );
}

function CoopFilterPanel({
  open,
  onClose,
  t,
  raw,
  sort,
  onSale,
  inStock,
  priceFrom,
  priceTo,
  priceCap,
  onApply,
}) {
  const [draft, setDraft] = useState({
    sort,
    onSale,
    inStock,
    priceFrom,
    priceTo,
  });

  useEffect(() => {
    if (!open) return undefined;
    setDraft({ sort, onSale, inStock, priceFrom, priceTo });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, sort, onSale, inStock, priceFrom, priceTo, onClose]);

  if (!open) return null;

  const sorts = [
    { id: 'sold', label: t.sortBest },
    { id: 'new', label: t.sortNew },
    { id: 'price-asc', label: t.sortPriceAsc },
    { id: 'price-desc', label: t.sortPriceDesc },
  ];

  const reset = () => {
    setDraft({
      sort: 'new',
      onSale: false,
      inStock: false,
      priceFrom: 0,
      priceTo: priceCap,
    });
  };

  return (
    <div className="shop-filter">
      <button type="button" className="shop-filter__backdrop" aria-label={raw.close} onClick={onClose} />
      <aside className="shop-filter__panel" role="dialog" aria-modal="true" aria-label={raw.coopShopFilters}>
        <div className="shop-filter__nav">
          <h2>{t.coopShopFilters}</h2>
          <button type="button" className="shop-coop-card__round" onClick={onClose} aria-label={raw.close}>
            ×
          </button>
        </div>

        <p className="shop-filter__label">{t.coopShopSort}</p>
        <div className="shop-filter__list">
          {sorts.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`shop-filter__choice${draft.sort === item.id ? ' is-on' : ''}`}
              onClick={() => setDraft((current) => ({ ...current, sort: item.id }))}
            >
              <span>{item.label}</span>
              <i />
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`shop-filter__toggle${draft.onSale ? ' is-on' : ''}`}
          onClick={() => setDraft((current) => ({ ...current, onSale: !current.onSale }))}
        >
          <span>{t.coopShopOnSale}</span>
          <i />
        </button>
        <button
          type="button"
          className={`shop-filter__toggle${draft.inStock ? ' is-on' : ''}`}
          onClick={() => setDraft((current) => ({ ...current, inStock: !current.inStock }))}
        >
          <span>{t.coopShopInStock}</span>
          <i />
        </button>

        <p className="shop-filter__label">{t.coopShopPrice}</p>
        <PriceRangeSlider
          min={0}
          max={priceCap}
          from={draft.priceFrom}
          to={draft.priceTo}
          onChange={(nextFrom, nextTo) => setDraft((current) => ({ ...current, priceFrom: nextFrom, priceTo: nextTo }))}
        />

        <div className="shop-filter__foot">
          <button type="button" className="shop-filter__reset" onClick={reset}>
            {t.coopShopReset}
          </button>
          <button
            type="button"
            className="shop-filter__done"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            {t.coopShopDone}
          </button>
        </div>
      </aside>
    </div>
  );
}

function coopSortOptions(t) {
  return [
    { id: 'sold', label: t.sortBest },
    { id: 'new', label: t.sortNew },
    { id: 'price-asc', label: t.sortPriceAsc },
    { id: 'price-desc', label: t.sortPriceDesc },
  ];
}

function CoopSortDropdown({ value, onChange }) {
  const { t } = useShopI18n();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const options = coopSortOptions(t);
  const current = options.find((item) => item.id === value) || options[1];

  useEffect(() => {
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="shop-coop__sort" ref={wrapRef}>
      <button
        type="button"
        className={`shop-coop__pill shop-coop__sort-btn${open || value !== 'new' ? ' is-on' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
      >
        <span>{t.coopShopSort}</span>
        <em>{current.label}</em>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="shop-coop__sort-menu" role="listbox">
          {options.map((item) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={item.id === value}
              className={item.id === value ? 'is-on' : ''}
              onClick={() => {
                onChange(item.id);
                setOpen(false);
              }}
            >
              <span>{item.label}</span>
              {item.id === value ? <i /> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CoopPriceDropdown({ from, to, cap, onChange, active }) {
  const { t, raw } = useShopI18n();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const maxValue = to == null ? cap : to;

  useEffect(() => {
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="shop-coop__price" ref={wrapRef}>
      <button
        type="button"
        className={`shop-coop__pill shop-coop__price-btn${open || active ? ' is-on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
      >
        <span>{t.coopShopPrice}</span>
        {active ? <em>{from} – {maxValue >= cap ? `${cap}+` : maxValue}</em> : null}
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="shop-coop__price-menu" role="dialog" aria-label={raw.coopShopPrice}>
          <PriceRangeSlider
            min={0}
            max={cap}
            from={from}
            to={maxValue}
            onChange={(nextFrom, nextTo) => onChange(nextFrom, nextTo >= cap ? null : nextTo)}
          />
        </div>
      )}
    </div>
  );
}

function CoopShopView({ catalog, stories = [] }) {
  const { t, raw, language } = useShopI18n();
  const { slug } = useParams();
  const productsRef = useRef(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('new');
  const [onSale, setOnSale] = useState(false);
  const [inStock, setInStock] = useState(false);
  const [priceFrom, setPriceFrom] = useState(0);
  const [priceTo, setPriceTo] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [followed, setFollowed] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setQuery('');
    setSort('new');
    setOnSale(false);
    setInStock(false);
    setPriceFrom(0);
    setPriceTo(null);
    setMenuOpen(false);
    setFiltersOpen(false);
    setProfile(null);
    setFollowed(isShopLoggedIn() && readFollowedCoops().includes(slug));
  }, [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    const applyFollows = (slugs) => {
      writeFollowedCoops(slugs);
      setFollowed(slugs.includes(slug));
    };
    const loadFollows = () => {
      if (!isShopLoggedIn()) {
        setFollowed(false);
        return;
      }
      fetchShopFollows()
        .then((data) => applyFollows((Array.isArray(data) ? data : []).map((item) => item.slug).filter(Boolean)))
        .catch(() => setFollowed(readFollowedCoops().includes(slug)));
    };
    loadFollows();
    window.addEventListener('taa-shop-auth', loadFollows);
    return () => window.removeEventListener('taa-shop-auth', loadFollows);
  }, [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    axios
      .get(`/api/shop/cooperatives/${slug}/`)
      .then((res) => {
        if (!cancelled) setProfile(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const fromCatalog = (catalog.cooperatives || []).find((item) => item.slug === slug);
  const products = (catalog.products || []).filter((item) => item.cooperative_slug === slug);
  const name = fromCatalog?.name || products[0]?.cooperative_name || '';
  const story = findCoopStory({ cooperative_slug: slug, cooperative_name: name }, stories);
  const coopName = name || profile?.name || story?.cooperative_name || story?.title || slug;
  const logo = profile?.image_url || fromCatalog?.image_url || products[0]?.cooperative_logo_url || story?.logo_url || '';
  const social = coopShopSocial(products, profile || fromCatalog);
  const deals = catalog.deals || [];
  const priceCap = coopPriceCap(products);
  const maxPrice = priceTo == null ? priceCap : priceTo;
  const q = query.trim().toLowerCase();
  const topSlug = profile?.top_product_slug || fromCatalog?.top_product_slug || mostRatedProduct(products)?.slug;
  const list = pinProductFirst(
    sortBrowseProducts(
      products.filter((product) => {
        const haystack = `${product.title} ${product.description || ''} ${product.category_name || ''}`;
        if (q && !haystack.toLowerCase().includes(q)) return false;
        if (onSale && !productHasPromo(product, deals)) return false;
        if (inStock && product.is_sold_out) return false;
        const price = Number(product.price || 0);
        if (priceFrom > 0 && price < priceFrom) return false;
        if (maxPrice < priceCap && price > maxPrice) return false;
        return true;
      }),
      sort
    ),
    sort === 'new' ? topSlug : ''
  );
  const pageUrl = buildAbsoluteUrl(coopShopPath(slug));
  const mark = (coopName || 'C').slice(0, 1).toUpperCase();
  const priceActive = priceFrom > 0 || maxPrice < priceCap;

  return (
    <>
      <SeoHead
        title={`${coopName} | Boutique des coopératives marocaines | Taaouniaty`}
        description={raw.coopShopSeoDesc(coopName)}
        keywords={`${coopName}, coopérative marocaine, boutique, marketplace, acheter, Taaouniaty`}
        url={pageUrl}
        image={logo || products[0]?.image_url}
        jsonLd={buildMarketplaceCoopJsonLd({
          coop: { slug, name: coopName, image_url: logo },
          products,
        })}
        language={language}
      />
      <section className="shop-coop">
        <div className="shop-coop__bar">
          <div className="shop-coop__chip">
            <span className="shop-coop__chip-logo">
              {logo ? <img src={logo} alt="" referrerPolicy="no-referrer" /> : mark}
            </span>
            <button
              type="button"
              className="shop-coop__menu-btn"
              aria-label={raw.coopShopMenu}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
          <button
            type="button"
            className={`shop-coop__follow${followed ? ' is-on' : ''}`}
            disabled={followBusy}
            onClick={async () => {
              if (!isShopLoggedIn()) {
                const next = encodeURIComponent(coopShopPath(slug));
                navigate(`/marketplace/account?mode=register&next=${next}&follow=${encodeURIComponent(slug)}`);
                return;
              }
              setFollowBusy(true);
              try {
                if (followed) {
                  await unfollowShopCoop(slug);
                  setFollowed(cacheFollowedSlug(slug, false));
                } else {
                  await followShopCoop(slug);
                  setFollowed(cacheFollowedSlug(slug, true));
                  showFollowToast(coopName);
                }
              } catch {
                /* keep current state */
              } finally {
                setFollowBusy(false);
              }
            }}
          >
            {followed ? t.coopShopFollowing : t.coopShopFollow}
          </button>
        </div>

        <header className="shop-coop__hero">
          <h1>{coopName}</h1>
          <p>
            {social.reviews ? (
              <>
                {social.rating.toFixed(1)} ★ {t.reviewsCount(social.reviews)}
              </>
            ) : (
              t.coopOwnerNew
            )}
          </p>
        </header>

        <div className="shop-coop__tools" ref={productsRef}>
          <div className="shop-coop__tools-top">
            <h2>{t.coopShopProducts}</h2>
            <label className="shop-coop__search">
              <SearchIcon />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={raw.coopShopSearch(coopName)}
                aria-label={raw.coopShopSearch(coopName)}
              />
            </label>
          </div>
          <div className="shop-coop__filters">
            <button type="button" className="shop-coop__filter-icon" aria-label={raw.coopShopFilters} onClick={() => setFiltersOpen(true)}>
              <FilterIcon />
            </button>
            <CoopSortDropdown value={sort} onChange={setSort} />
            <button
              type="button"
              className={`shop-coop__pill${onSale ? ' is-on' : ''}`}
              onClick={() => setOnSale((value) => !value)}
            >
              {t.coopShopOnSale}
            </button>
            <CoopPriceDropdown
              from={priceFrom}
              to={priceTo}
              cap={priceCap}
              active={priceActive}
              onChange={(nextFrom, nextTo) => {
                setPriceFrom(nextFrom);
                setPriceTo(nextTo);
              }}
            />
            <button
              type="button"
              className={`shop-coop__pill${inStock ? ' is-on' : ''}`}
              onClick={() => setInStock((value) => !value)}
            >
              {t.coopShopInStock}
            </button>
          </div>
        </div>

        {list.length ? (
          <div className="shop-coop__grid">
            {list.map((product) => (
              <ShopTile key={product.id || product.slug} product={product} />
            ))}
          </div>
        ) : (
          <div className="shop-browse__empty">
            <p>{products.length ? t.emptyProducts : t.coopShopMissing}</p>
            <Link to="/marketplace/browse">{t.allProducts}</Link>
          </div>
        )}
      </section>
      <CoopFilterPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        t={t}
        raw={raw}
        sort={sort}
        onSale={onSale}
        inStock={inStock}
        priceFrom={priceFrom}
        priceTo={maxPrice}
        priceCap={priceCap}
        onApply={(next) => {
          setSort(next.sort);
          setOnSale(next.onSale);
          setInStock(next.inStock);
          setPriceFrom(next.priceFrom);
          setPriceTo(next.priceTo >= priceCap ? null : next.priceTo);
        }}
      />
      <CoopProfileCard
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        profile={profile}
        slug={slug}
      />
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function ReviewCarousel({ reviews }) {
  const { t, raw, isAr } = useShopI18n();
  const scrollerRef = useRef(null);
  const dragRef = useRef(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    pausedRef.current = false;
    setIndex(0);
    scrollerRef.current?.scrollTo({ left: 0 });
  }, [reviews.length]);

  useEffect(() => {
    if (reviews.length < 2) return undefined;
    const media = window.matchMedia('(max-width: 980px)');
    if (!media.matches) return undefined;
    const timer = window.setInterval(() => {
      if (pausedRef.current) return;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const next = (indexRef.current + 1) % reviews.length;
      const width = scroller.clientWidth;
      scroller.scrollTo({
        left: (isAr ? -1 : 1) * next * width,
        behavior: 'smooth',
      });
      setIndex(next);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [reviews.length, isAr]);

  if (!reviews.length) return <p>{t.noReviews}</p>;

  const pauseAuto = () => {
    pausedRef.current = true;
  };

  const slideTo = (next) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const count = reviews.length;
    const clamped = ((next % count) + count) % count;
    const width = scroller.clientWidth;
    scroller.scrollTo({
      left: (isAr ? -1 : 1) * clamped * width,
      behavior: 'smooth',
    });
    setIndex(clamped);
  };

  const onScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const width = scroller.clientWidth || 1;
    const next = Math.round(Math.abs(scroller.scrollLeft) / width);
    setIndex(Math.min(reviews.length - 1, Math.max(0, next)));
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse') {
      pauseAuto();
      return;
    }
    const scroller = scrollerRef.current;
    if (!scroller || reviews.length < 2) return;
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      left: scroller.scrollLeft,
      moved: false,
    };
    scroller.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    if (!drag || !scroller || event.pointerId !== drag.id) return;
    const delta = event.clientX - drag.x;
    if (Math.abs(delta) > 6) drag.moved = true;
    if (drag.moved) scroller.scrollLeft = drag.left - delta;
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    if (!drag || event.pointerId !== drag.id) return;
    dragRef.current = null;
    if (!scroller) return;
    const width = scroller.clientWidth || 1;
    slideTo(Math.round(Math.abs(scroller.scrollLeft) / width));
  };

  return (
    <div
      className={`shop-pdp__review-carousel${reviews.length > 1 ? ' is-many' : ''}`}
      onTouchStart={pauseAuto}
    >
      <div
        className="shop-pdp__review-cards"
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {reviews.map((review) => (
          <article key={`${review.name}-${review.text}`}>
            <span className="shop-pdp__stars" aria-hidden="true">{'★'.repeat(review.rating || 5)}</span>
            {review.text ? <p>{review.text}</p> : null}
            <small>
              <i style={{ background: review.tone }}>{review.name.slice(0, 1)}</i>
              {review.name}
            </small>
          </article>
        ))}
      </div>
      {reviews.length > 1 ? (
        <>
          <button type="button" className="shop-pdp__nav shop-pdp__nav--prev" aria-label={raw.prevReview} onClick={() => slideTo(index - 1)}>
            ‹
          </button>
          <button type="button" className="shop-pdp__nav shop-pdp__nav--next" aria-label={raw.nextReview} onClick={() => slideTo(index + 1)}>
            ›
          </button>
          <div className="shop-pdp__review-dots" role="tablist" aria-label={raw.reviewsCount(reviews.length)}>
            {reviews.map((review, dot) => (
              <button
                key={`${review.name}-${dot}`}
                type="button"
                className={dot === index ? 'is-on' : ''}
                aria-label={`${dot + 1} / ${reviews.length}`}
                onClick={() => slideTo(dot)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function isDetailedProduct(product) {
  return Boolean(
    product &&
      (Array.isArray(product.gallery) || Object.prototype.hasOwnProperty.call(product, 'description'))
  );
}

function ProductSkeleton({ product }) {
  return (
    <article className="shop-pdp shop-pdp--boot" aria-busy="true">
      <div className="shop-pdp__stage">
        <div className="shop-pdp__gallery">
          <div className="shop-pdp__photo-wrap">
            <div className="shop-pdp__photo">
              {product?.image_url ? (
                <img src={product.image_url} alt="" fetchPriority="high" decoding="async" referrerPolicy="no-referrer" />
              ) : null}
            </div>
          </div>
        </div>
        <div className="shop-pdp__info">
          {product?.cooperative_name ? (
            <p className="shop-pdp__proof">{product.cooperative_name}</p>
          ) : (
            <div className="shop-pdp__boot-kicker" />
          )}
          <h1>{product?.title || ''}</h1>
          <div className="shop-pdp__boot-line" />
          {product?.price != null ? (
            <div className="shop-pdp__price">
              <strong>{Number(product.price).toFixed(2)} {product.currency || 'MAD'}</strong>
            </div>
          ) : (
            <div className="shop-pdp__boot-price" />
          )}
          <div className="shop-pdp__add shop-pdp__boot-btn" />
          <div className="shop-pdp__buy shop-pdp__boot-btn shop-pdp__boot-btn--dark" />
        </div>
      </div>
    </article>
  );
}

function ProductDetail({ onAdd, catalog, stories = [] }) {
  const { t, raw, language } = useShopI18n();
  const { slug } = useParams();
  const navigate = useNavigate();
  const catalogProduct = (catalog.products || []).find((item) => item.slug === slug);
  const [data, setData] = useState(() => {
    const cached = readCachedProduct(slug);
    if (isDetailedProduct(cached?.product) && cached.product.slug === slug) return cached;
    return null;
  });
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = readCachedProduct(slug);
    const fromCatalog = (catalog.products || []).find((item) => item.slug === slug);
    if (isDetailedProduct(cached?.product) && cached.product.slug === slug) {
      setData(cached);
      setError('');
    }
    prefetchShopProduct(slug, { shallow: true })
      .then((payload) => {
        if (!cancelled) {
          setData(payload);
          setError('');
        }
      })
      .catch(() => {
        if (!cancelled && !fromCatalog && !cached) setError(t.notFound);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, catalogProduct?.slug, t.notFound]);

  useEffect(() => {
    setActiveImage(0);
    setQty(1);
    setSaved(readSaved().includes(slug));
  }, [slug]);

  useEffect(() => {
    if (data?.product?.slug === slug) {
      trackMetaViewContent(data.product);
    }
  }, [slug, data]);

  if (error) return <p className="shop-empty">{error}</p>;
  const payload = isDetailedProduct(data?.product) && data.product.slug === slug ? data : null;
  if (!payload) {
    return <ProductSkeleton product={readCachedProduct(slug)?.product || catalogProduct} />;
  }

  const product = payload.product;
  const soldOut = Boolean(product.is_sold_out);
  const social = productSocial(product);
  const off = discountPercent(product);
  const brand = product.cooperative_name || 'taaouniaty';
  const images = productGallery(product);
  const currentMedia = images[activeImage] || null;
  const reviews = (payload.reviews || []).map((review) => ({
    name: review.author_name || t.client,
    text: review.comment || '',
    tone: '#f3c6a8',
    rating: review.rating,
  }));
  const ratingBars = [5, 4, 3, 2, 1].map((star) => {
    const total = reviews.length || 1;
    const count = reviews.filter((review) => Number(review.rating) === star).length;
    return { star, width: reviews.length ? Math.round((count / total) * 100) : 0 };
  });
  const story = findCoopStory(product, stories);
  const brandTo = isMarketplaceCoopOwner(product)
    ? coopShopPath(product.cooperative_slug || story?.catalog_slug)
    : '';
  const catalogSlug = story?.catalog_slug || product.cooperative_slug || '';
  const detailsTo = catalogSlug ? `/${catalogSlug}` : ARTICLE_COOP_LIST;
  const detailsName = product.cooperative_name || story?.cooperative_name || story?.title || 'taaouniaty';
  const liked = relatedForProduct(product, catalog, payload.related);
  const pageUrl = buildAbsoluteUrl(`/marketplace/p/${product.slug}`);
  const seoDescription =
    product.description || raw.seoProductDesc(product.title, brand);

  const share = async () => {
    const payload = { title: product.title, text: seoDescription, url: pageUrl };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(pageUrl);
      }
      /* keep label as Partager */
    } catch {
      /* ignore cancel */
    }
  };

  const toggleSave = () => {
    setSaved(saveOrUnsave(product));
  };

  return (
    <>
      <SeoHead
        title={
          product.category_name
            ? `${product.title} | ${product.category_name} | Taaouniaty`
            : `${product.title} | Acheter en ligne | Taaouniaty`
        }
        description={seoDescription}
        keywords={`${product.title}, ${product.category_name || ''}, ${brand}, acheter, coopératives marocaines, Taaouniaty, Maroc`}
        url={pageUrl}
        image={product.image_url}
        type="product"
        jsonLd={buildMarketplaceProductJsonLd({ ...product, rating: social.rating, reviews: social.reviews, brand })}
        language={language}
      />
      <article className="shop-pdp">
        <div className="shop-pdp__stage">
          <div className="shop-pdp__gallery">
            <div className="shop-pdp__photo-wrap">
              <div className="shop-pdp__photo">
                {currentMedia?.type === 'video' ? (
                  <video
                    key={currentMedia.url}
                    src={currentMedia.url}
                    controls
                    playsInline
                    preload="metadata"
                    poster={videoPosterUrl(currentMedia.url)}
                  />
                ) : currentMedia?.url ? (
                  <img src={currentMedia.url} alt={product.title} loading="eager" fetchPriority="high" decoding="async" referrerPolicy="no-referrer" />
                ) : (
                  <MarketplaceLogo />
                )}
              </div>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="shop-pdp__nav shop-pdp__nav--prev"
                    aria-label={raw.prevPhoto}
                    onClick={() => setActiveImage((index) => (index - 1 + images.length) % images.length)}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="shop-pdp__nav shop-pdp__nav--next"
                    aria-label={raw.nextPhoto}
                    onClick={() => setActiveImage((index) => (index + 1) % images.length)}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="shop-pdp__thumbs">
                {images.map((item, index) => (
                  <button
                    key={`${item.url}-${index}`}
                    type="button"
                    className={`shop-pdp__thumb${index === activeImage ? ' is-active' : ''}`}
                    aria-label={item.type === 'video' ? raw.galleryVideo : raw.galleryPhoto}
                    onClick={() => setActiveImage(index)}
                  >
                    <img src={item.type === 'video' ? videoPosterUrl(item.url) : item.url} alt="" referrerPolicy="no-referrer" />
                    {item.type === 'video' ? <span className="shop-pdp__thumb-play">▶</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="shop-pdp__info">
            <div className="shop-pdp__brand">
              {brandTo ? (
                <Link className="shop-pdp__brand-mark" to={brandTo} aria-label={brand}>
                  {product.cooperative_logo_url ? (
                    <img src={product.cooperative_logo_url} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <MarketplaceLogo />
                  )}
                </Link>
              ) : (
                <span className="shop-pdp__brand-mark">
                  {product.cooperative_logo_url ? (
                    <img src={product.cooperative_logo_url} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <MarketplaceLogo />
                  )}
                </span>
              )}
              <div>
                {brandTo ? <Link className="shop-pdp__brand-name" to={brandTo}>{brand}</Link> : <strong>{brand}</strong>}
                <span>
                  {social.rating.toFixed(1)} ({social.reviews})
                </span>
              </div>
              <ProductMoreMenu product={product} ownerName={brand} />
            </div>
            <h1>{product.title}</h1>
            <p className="shop-pdp__ratings">
              <span className="shop-pdp__stars" aria-hidden="true">★★★★★</span>
              {t.reviewsCount(social.reviews)}
            </p>
            <p className="shop-pdp__proof">{t.deliveryEverywhere}</p>
            <div className="shop-pdp__price">
              <strong>{formatPrice(product)}</strong>
              {product.compare_at_price && Number(product.compare_at_price) > Number(product.price) && (
                <s>{Number(product.compare_at_price).toFixed(2)} {product.currency || 'MAD'}</s>
              )}
              {off > 0 && <span>{t.percentOff(off)}</span>}
              {product.is_highlighted && <span>{t.featured}</span>}
              {product.has_free_delivery && <span>{t.freeDelivery}</span>}
              {soldOut && <span className="shop-pdp__sold">{t.soldOut}</span>}
            </div>
            <label className="shop-pdp__qty">
              {t.qty}
              <span className="shop-pdp__stepper">
                <button type="button" disabled={soldOut} onClick={() => setQty((value) => Math.max(1, value - 1))} aria-label={raw.minus}>−</button>
                <em>{qty}</em>
                <button type="button" disabled={soldOut} onClick={() => setQty((value) => Math.min(20, value + 1))} aria-label={raw.plus}>+</button>
              </span>
            </label>
            <button type="button" className="shop-pdp__add" disabled={soldOut} onClick={() => onAdd(product, qty)}>
              {soldOut ? t.soldOut : t.addToCart}
            </button>
            <button
              type="button"
              className="shop-pdp__buy"
              disabled={soldOut}
              onPointerDown={() => {
                const next = readCart();
                const existing = next.find((item) => item.slug === product.slug);
                const preview = existing
                  ? next.map((item) => (item.slug === product.slug ? { ...item, qty: item.qty + qty } : item))
                  : [...next, { slug: product.slug, qty }];
                prefetchCheckoutQuote(preview).catch(() => {});
              }}
              onClick={() => {
                onAdd(product, qty);
                prefetchCheckoutQuote(readCart()).catch(() => {});
                navigate('/marketplace/cart');
              }}
            >
              {soldOut ? t.soldOut : t.buy}
            </button>
            <div className="shop-pdp__actions">
              <button type="button" className="shop-pdp__save" onClick={toggleSave}>
                <HeartIcon filled={saved} />
                {saved ? t.savedLabel : t.save}
              </button>
              <button type="button" onClick={share}>
                <ShareIcon />
                {t.share}
              </button>
            </div>

            <section className="shop-pdp__block">
              <h2>{t.description}</h2>
              <p>
                {product.description || t.descFallback(product.title, brand)}
              </p>
              <Link className="shop-pdp__more" to={detailsTo}>
                <LinkIcon />
                {t.moreDetails(detailsName)}
              </Link>
            </section>

            <section className="shop-pdp__reviews">
              <div className="shop-pdp__review-head">
                <div>
                  <strong>{social.reviews ? social.rating.toFixed(1) : '—'}</strong>
                  <span className="shop-pdp__stars" aria-hidden="true">★★★★★</span>
                  <p>{t.reviewsCount(social.reviews)}</p>
                </div>
                <ul className="shop-pdp__bars">
                  {ratingBars.map((bar) => (
                    <li key={bar.star}>
                      <span>{bar.star}</span>
                      <i><b style={{ width: `${bar.width}%` }} /></i>
                    </li>
                  ))}
                </ul>
              </div>
              <ReviewCarousel reviews={reviews} />
            </section>

            <section className={`shop-pdp__block${isMarketplaceCoopOwner(product) ? '' : ' shop-pdp__block--last'}`}>
              <h2>{t.shippingReturns}</h2>
              <p>{t.shippingReturnsText}</p>
            </section>
            <CoopOwnerCard product={product} catalog={catalog} story={story} />
          </div>
        </div>
        {liked.length > 0 && (
          <section className="shop-pdp__liked">
            <h2 className="shop-rail__head">{t.alsoLike}</h2>
            <div className="shop-pdp__liked-grid">
              {liked.map((item) => (
                <ShopTile key={item.id || item.slug} product={item} />
              ))}
            </div>
          </section>
        )}
      </article>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function enrichCartItems(items, catalog) {
  const products = catalog?.products || [];
  return items.map((item) => {
    const product = products.find((entry) => entry.slug === item.slug);
    if (!product) return item;
    return {
      ...item,
      title: product.title || item.title,
      price: product.price ?? item.price,
      currency: product.currency || item.currency || 'MAD',
      image_url: product.image_url || item.image_url,
      compare_at_price: product.compare_at_price ?? item.compare_at_price,
      cooperative_name: product.cooperative_name || item.cooperative_name,
      category_slug: product.category_slug || item.category_slug,
      is_sold_out: Boolean(product.is_sold_out),
    };
  });
}

function CartView({ items, catalog, onRemove, onQty }) {
  const { t, raw, language } = useShopI18n();
  const [form, setForm] = useState({ customer_name: '', phone: '', city: '', address: '', notes: '' });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const lines = enrichCartItems(items, catalog);
  const hasSoldOut = lines.some((item) => item.is_sold_out);
  const currency = lines[0]?.currency || 'MAD';
  const count = lines.reduce((sum, item) => sum + item.qty, 0);
  const total = lines.reduce((sum, item) => sum + Number(item.price || 0) * item.qty, 0);
  const deliveryFee = quote ? Number(quote.delivery_fee || 0) : null;
  const grandTotal = quote ? Number(quote.total || total) : total;
  const deliveryLabel = deliveryFee == null
    ? t.toConfirm
    : deliveryFee <= 0
      ? t.deliveryFree
      : `${deliveryFee.toFixed(2)} ${currency}`;
  const liked = (catalog?.products || [])
    .filter((product) => !lines.some((item) => item.slug === product.slug))
    .slice(0, 8);

  useEffect(() => {
    const customer = getShopCustomer();
    if (!customer) return;
    setForm((current) => ({
      ...current,
      customer_name: current.customer_name || customer.name || '',
      phone: current.phone || customer.phone || '',
      city: current.city || customer.city || '',
      address: current.address || customer.address || '',
    }));
  }, []);

  const linesKey = lines.map((item) => `${item.slug}:${item.qty}`).join(',');

  useEffect(() => {
    if (!lines.length) {
      setQuote(null);
      return undefined;
    }
    const cached = readCachedQuote(lines, form.city);
    if (cached) setQuote(cached);
    const timer = window.setTimeout(() => {
      prefetchCheckoutQuote(lines, form.city)
        .then((data) => setQuote(data || null))
        .catch(() => {
          if (!cached) setQuote(null);
        });
    }, cached || !form.city.trim() ? 0 : 150);
    return () => window.clearTimeout(timer);
  }, [linesKey, form.city]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (hasSoldOut) {
      setError(t.soldOutCart);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post('/api/shop/orders/', {
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        items: lines.map((item) => ({ slug: item.slug, qty: item.qty })),
      });
      writeCart([]);
      setDone(res.data);
      trackMetaPurchase(res.data, lines);
    } catch (err) {
      const data = err.response?.data || {};
      setError(
        data.customer_name ||
          data.phone ||
          data.address ||
          data.items ||
          data.detail ||
          t.orderFail
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <SeoHead noindex title={raw.orderDoneSeo} description="" language={language} />
        <div className="shop-bag shop-bag--done">
          <i className="fas fa-check-circle shop-bag--done-check" aria-hidden />
          <h1>{t.orderDoneTitle}</h1>
          <p>{t.orderDoneText(done.customer_name)}</p>
          <div className="shop-bag__cta-row">
            {done.receipt_token ? (
              <Link className="shop-bag__btn" to={`/marketplace/order/${done.receipt_token}`}>
                {t.viewReceipt}
              </Link>
            ) : null}
            <Link className={`shop-bag__btn${done.receipt_token ? ' shop-bag__btn--ghost' : ''}`} to="/marketplace">
              {t.back}
            </Link>
          </div>
        </div>
        <div className="shop-pdp__end">
          <ShopFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHead noindex title={raw.cartSeoTitle} description={raw.cartSeoDesc} language={language} />
      <div className="shop-bag">
        <div className="shop-bag__head">
          <h1>{t.cart}</h1>
          {count > 0 && <p>{t.articles(count)}</p>}
        </div>

        {lines.length === 0 ? (
          <div className="shop-bag__empty">
            <p>{t.emptyCart}</p>
            <Link className="shop-bag__btn" to="/marketplace/browse">{t.browseProducts}</Link>
          </div>
        ) : (
          <div className="shop-bag__layout">
            <div className="shop-bag__list">
              {lines.map((item) => {
                const off = discountPercent(item);
                const line = Number(item.price || 0) * item.qty;
                return (
                  <article key={item.slug} className={`shop-bag__item${item.is_sold_out ? ' is-sold-out' : ''}`}>
                    <Link to={`/marketplace/p/${item.slug}`} className="shop-bag__photo">
                      {item.image_url ? <img src={item.image_url} alt="" referrerPolicy="no-referrer" /> : <MarketplaceLogo />}
                    </Link>
                    <div className="shop-bag__meta">
                      <Link to={`/marketplace/p/${item.slug}`}>
                        {item.cooperative_name && <small>{item.cooperative_name}</small>}
                        <strong>{item.title}</strong>
                      </Link>
                      {item.is_sold_out && <p className="shop-bag__sold">{t.soldOut}</p>}
                      <div className="shop-bag__price">
                        <b>{Number(item.price || 0).toFixed(2)} {item.currency || currency}</b>
                        {off > 0 && <s>{Number(item.compare_at_price).toFixed(2)} {item.currency || currency}</s>}
                        {off > 0 && <em>{t.percentOff(off)}</em>}
                      </div>
                      <div className="shop-bag__tools">
                        <span className="shop-bag__qty">
                          <button type="button" disabled={item.is_sold_out} onClick={() => onQty(item.slug, item.qty - 1)} aria-label={raw.minus}>−</button>
                          <i>{item.qty}</i>
                          <button type="button" disabled={item.is_sold_out} onClick={() => onQty(item.slug, Math.min(20, item.qty + 1))} aria-label={raw.plus}>+</button>
                        </span>
                        <button type="button" className="shop-bag__remove" onClick={() => onRemove(item.slug)}>
                          {t.remove}
                        </button>
                      </div>
                    </div>
                    <p className="shop-bag__line">{line.toFixed(2)} {item.currency || currency}</p>
                  </article>
                );
              })}
            </div>

            <aside className="shop-bag__sum">
              <h2>{t.summary}</h2>
              <ul>
                <li><span>{t.subtotal}</span><strong>{total.toFixed(2)} {currency}</strong></li>
                <li>
                  <span>{t.delivery}{quote?.delivery_zone_label ? ` · ${quote.delivery_zone_label}` : ''}</span>
                  <strong>{deliveryLabel}</strong>
                </li>
                {quote?.message && <li className="shop-bag__promo"><span>{quote.message}</span></li>}
                <li className="is-total"><span>{t.total}</span><strong>{grandTotal.toFixed(2)} {currency}</strong></li>
              </ul>
              <p className="shop-bag__trust">{t.trustLine}</p>
              <form className="shop-bag__form" onSubmit={submit}>
                <label>
                  {t.name}
                  <input value={form.customer_name} onChange={(e) => setField('customer_name', e.target.value)} required />
                </label>
                <label>
                  {t.phone}
                  <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} required />
                </label>
                <label>
                  {t.city}
                  <input
                    value={form.city}
                    onChange={(e) => setField('city', e.target.value)}
                    placeholder={raw.cityPlaceholder}
                    required
                  />
                </label>
                <label>
                  {t.address}
                  <input
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    placeholder={raw.addressPlaceholder}
                    required
                  />
                </label>
                <label>
                  {t.noteOptional}
                  <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} />
                </label>
                {error && <p className="shop-bag__error">{error}</p>}
                <button type="submit" className="shop-bag__btn" disabled={busy || hasSoldOut}>
                  {busy ? t.sending : t.order}
                </button>
              </form>
            </aside>
          </div>
        )}

        {liked.length > 0 && (
          <section className="shop-pdp__liked">
            <h2 className="shop-rail__head">{t.alsoLike}</h2>
            <div className="shop-pdp__liked-grid">
              {liked.map((product) => (
                <ShopTile key={product.id || product.slug} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

function formatShopDate(value, locale = 'fr-FR') {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

function AccountView({ catalog }) {
  const { t, raw, language } = useShopI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = String(params.get('next') || '').startsWith('/marketplace') ? params.get('next') : '';
  const followSlug = String(params.get('follow') || '').trim();
  const [customer, setCustomer] = useState(() => getShopCustomer());
  const [mode, setMode] = useState(() => (params.get('mode') === 'register' || followSlug ? 'register' : 'login'));
  const [tab, setTab] = useState('home');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', city: '', address: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [collections, setCollections] = useState({ collections: [], saved: [] });
  const [follows, setFollows] = useState([]);

  const refresh = () => {
    if (!isShopLoggedIn()) return;
    fetchShopMe()
      .then(setMe)
      .catch(() => {});
    fetchShopOrders()
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]));
    fetchShopCollections()
      .then((data) => setCollections(data || { collections: [], saved: [] }))
      .catch(() => setCollections({ collections: [], saved: [] }));
    fetchShopFollows()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        writeFollowedCoops(list.map((item) => item.slug).filter(Boolean));
        setFollows(list);
      })
      .catch(() => setFollows([]));
  };

  useEffect(() => {
    const sync = () => {
      const next = getShopCustomer();
      setCustomer(next);
      if (next) refresh();
    };
    sync();
    window.addEventListener('taa-shop-auth', sync);
    window.addEventListener('taa-shop-follows', sync);
    return () => {
      window.removeEventListener('taa-shop-auth', sync);
      window.removeEventListener('taa-shop-follows', sync);
    };
  }, []);

  useEffect(() => {
    if (!customer || (!followSlug && !nextPath)) return undefined;
    let cancelled = false;
    (async () => {
      if (followSlug) {
        try {
          await followShopCoop(followSlug);
          cacheFollowedSlug(followSlug, true);
          showFollowToast();
        } catch {
          /* ignore */
        }
      }
      if (!cancelled && nextPath) navigate(nextPath, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [customer, followSlug, nextPath, navigate]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submitAuth = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'register') {
        await shopRegister({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          password: form.password,
        });
      } else {
        await shopLogin({
          identifier: form.email.trim() || form.phone.trim(),
          password: form.password,
        });
      }
      setCustomer(getShopCustomer());
      setForm((current) => ({ ...current, password: '' }));
      refresh();
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.detail || data.email || data.phone || data.password || data.name || t.loginFail);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await shopLogout();
    setCustomer(null);
    setMe(null);
    setOrders([]);
    setFollows([]);
    setTab('home');
  };

  const products = catalog?.products || [];
  const localSaved = readSaved()
    .map((slug) => products.find((item) => item.slug === slug))
    .filter(Boolean);

  if (!customer) {
    return (
      <>
        <SeoHead noindex title={raw.accountSeoLogin} description="" language={language} />
        <div className="shop-account">
          <h1>{t.yourSpace}</h1>
          <p className="shop-account__lead">{followSlug ? t.accountLeadFollow : t.accountLead}</p>
          <div className="shop-account__switch">
            <button type="button" className={mode === 'login' ? 'is-active' : ''} onClick={() => setMode('login')}>
              {t.signIn}
            </button>
            <button type="button" className={mode === 'register' ? 'is-active' : ''} onClick={() => setMode('register')}>
              {t.createAccount}
            </button>
          </div>
          <form className="shop-account__form" onSubmit={submitAuth}>
            {mode === 'register' && (
              <label>
                {t.name}
                <input value={form.name} onChange={(event) => setField('name', event.target.value)} required />
              </label>
            )}
            <label>
              {t.email}
              <input type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} required={mode === 'register'} />
            </label>
            {mode === 'register' && (
              <label>
                {t.phone}
                <input value={form.phone} onChange={(event) => setField('phone', event.target.value)} required />
              </label>
            )}
            {mode === 'login' && !form.email && (
              <label>
                {t.phone}
                <input value={form.phone} onChange={(event) => setField('phone', event.target.value)} />
              </label>
            )}
            <label>
              {t.password}
              <input type="password" value={form.password} onChange={(event) => setField('password', event.target.value)} required />
            </label>
            {error ? <p className="shop-account__error">{error}</p> : null}
            <button type="submit" className="shop-account__submit" disabled={busy}>
              {busy ? '…' : mode === 'register' ? t.createMyAccount : t.loginAction}
            </button>
          </form>
        </div>
        <div className="shop-pdp__end">
          <ShopFooter />
        </div>
      </>
    );
  }

  const stats = me || {};
  const allSaved = [
    ...(collections.saved || []),
    ...(collections.collections || []).flatMap((col) => col.items || []),
  ];
  const followedCoops = (follows || []).map((follow) => {
    const fromCatalog = (catalog?.cooperatives || []).find((item) => item.slug === follow.slug);
    const product = products.find((item) => item.cooperative_slug === follow.slug);
    return {
      slug: follow.slug,
      name: follow.name || fromCatalog?.name || product?.cooperative_name || follow.slug,
      logo: fromCatalog?.image_url || product?.cooperative_logo_url || '',
    };
  }).filter((item) => item.slug);

  return (
    <>
      <SeoHead noindex title={raw.accountSeoMe} description="" language={language} />
      <div className="shop-account">
        <div className="shop-account__head">
          <div>
            <p className="shop-account__hello">{t.hello}</p>
            <h1>{customer.name}</h1>
          </div>
          <button type="button" className="shop-account__out" onClick={logout}>
            {t.logout}
          </button>
        </div>

        <div className="shop-account__tabs">
          {[
            ['home', t.overview],
            ['follows', t.followed],
            ['orders', t.orders],
            ['receipts', t.receipts],
            ['saved', t.collections],
          ].map(([id, label]) => (
            <button key={id} type="button" className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'home' && (
          <>
          <div className="shop-account__grid">
            <div className="shop-account__stat">
              <strong>{stats.order_count || orders.length || 0}</strong>
              <span>{t.orders}</span>
            </div>
            <div className="shop-account__stat">
              <strong>{stats.saved_count || allSaved.length || localSaved.length}</strong>
              <span>{t.saved}</span>
            </div>
            <div className="shop-account__stat">
              <strong>{followedCoops.length}</strong>
              <span>{t.followed}</span>
            </div>
            <div className="shop-account__stat">
              <strong>{stats.collection_count || (collections.collections || []).length}</strong>
              <span>{t.collections}</span>
            </div>
          </div>
          {followedCoops.length ? (
            <div className="shop-account__follows">
              <p className="shop-account__label">{t.followed}</p>
              {followedCoops.map((coop) => (
                <Link key={coop.slug} className="shop-account__coop" to={coopShopPath(coop.slug)}>
                  <span className="shop-account__coop-logo">
                    {coop.logo ? <img src={coop.logo} alt="" referrerPolicy="no-referrer" /> : (coop.name || 'C').slice(0, 1).toUpperCase()}
                  </span>
                  <strong>{coop.name}</strong>
                  <span aria-hidden="true">›</span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="shop-account__empty">{t.noFollowed}</p>
          )}
          </>
        )}

        {tab === 'follows' && (
          followedCoops.length ? (
            <div className="shop-account__follows">
              {followedCoops.map((coop) => (
                <Link key={coop.slug} className="shop-account__coop" to={coopShopPath(coop.slug)}>
                  <span className="shop-account__coop-logo">
                    {coop.logo ? <img src={coop.logo} alt="" referrerPolicy="no-referrer" /> : (coop.name || 'C').slice(0, 1).toUpperCase()}
                  </span>
                  <strong>{coop.name}</strong>
                  <span aria-hidden="true">›</span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="shop-account__empty">{t.noFollowed}</p>
          )
        )}

        {tab === 'orders' && (
          <div className="shop-account__list">
            {orders.length ? orders.map((order) => (
              <article key={order.id} className="shop-account__card">
                <div>
                  <strong>{order.reference}</strong>
                  <p>{formatShopDate(order.created_at, raw.dateLocale)} · {order.status_label}</p>
                </div>
                <b>{Number(order.total).toFixed(2)} {order.currency}</b>
              </article>
            )) : <p className="shop-account__empty">{t.noOrders}</p>}
          </div>
        )}

        {tab === 'receipts' && (
          <div className="shop-account__list">
            {orders.length ? orders.map((order) => (
              <article key={order.id} className="shop-account__card">
                <div>
                  <strong>{t.receiptLabel(order.reference)}</strong>
                  <p>{formatShopDate(order.created_at, raw.dateLocale)}</p>
                </div>
                {order.receipt_path ? <Link to={order.receipt_path}>{t.viewReceiptLink}</Link> : null}
              </article>
            )) : <p className="shop-account__empty">{t.noReceipts}</p>}
          </div>
        )}

        {tab === 'saved' && (
          <div className="shop-account__saved">
            {(collections.collections || []).map((collection) => {
              const items = (collection.items || [])
                .map((item) => products.find((entry) => entry.slug === item.slug) || item)
                .filter((item) => item?.slug);
              return <ProductRail key={collection.id} title={collection.name} products={items} />;
            })}
            <ProductRail
              title={t.saved}
              products={(collections.saved?.length ? collections.saved : localSaved)
                .map((item) => products.find((entry) => entry.slug === item.slug) || item)
                .filter((item) => item?.slug)}
            />
            {!collections.collections?.length && !collections.saved?.length && !localSaved.length ? (
              <p className="shop-account__empty">{t.noSaved}</p>
            ) : null}
          </div>
        )}
      </div>
      <div className="shop-pdp__end">
        <ShopFooter />
      </div>
    </>
  );
}

export default function MarketplacePage({ language = 'fr', setLanguage }) {
  const t = useMemo(() => bilingualShopCopy(), []);
  const i18n = useMemo(
    () => ({
      language: 'fr',
      setLanguage: (next) => setLanguage?.(next),
      t,
      isAr: false,
    }),
    [setLanguage, t]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(() => readCachedCatalog() || EMPTY_SHOP_CATALOG);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState(readCart);
  const [saved, setSaved] = useState(readSaved);
  const [coopStories, setCoopStories] = useState([]);
  const [socialProof, setSocialProof] = useState(() => readCachedCatalog()?.social_proof || []);
  const isHome = location.pathname === '/marketplace' || location.pathname === '/marketplace/';
  const isProduct = location.pathname.includes('/marketplace/p/');
  const isCoopShop = location.pathname.startsWith('/marketplace/c/');
  const isBrowse = location.pathname.startsWith('/marketplace/browse');
  const isDeals = location.pathname.startsWith('/marketplace/deals');
  const isCart = location.pathname.startsWith('/marketplace/cart');
  const isSaved = location.pathname.startsWith('/marketplace/saved');
  const isAccount = location.pathname.startsWith('/marketplace/account');

  const goSearch = () => {
    const q = search.trim();
    navigate(q ? `/marketplace/browse?q=${encodeURIComponent(q)}` : '/marketplace/browse');
  };

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      axios
        .get('/api/articles/cooperatives/', { params: { language: language === 'ar' ? 'ar' : 'fr', limit: 80 } })
        .then((res) => {
          if (!cancelled) setCoopStories(res.data?.results || []);
        })
        .catch(() => {
          if (!cancelled) setCoopStories([]);
        });
    }, 1800);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    const landingSlug = productSlugFromPath();
    const afterProduct = landingSlug
      ? prefetchShopProduct(landingSlug, { shallow: true }).catch(() => null)
      : Promise.resolve();

    const loadSocialProof = () => {
      if (cancelled) return;
      axios
        .get('/api/shop/social-proof/')
        .then((res) => {
          if (!cancelled) setSocialProof(res.data?.results || []);
        })
        .catch(() => {});
    };

    afterProduct.finally(() => {
      if (cancelled) return;
      prefetchShopCatalog()
        .then((data) => {
          if (cancelled) return;
          setCatalog(data || EMPTY_SHOP_CATALOG);
          if (data?.social_proof?.length) setSocialProof(data.social_proof);
        })
        .catch(() => {
          if (!cancelled && !readCachedCatalog()) setCatalog(EMPTY_SHOP_CATALOG);
        });
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(loadSocialProof, { timeout: 2500 });
      } else {
        window.setTimeout(loadSocialProof, 900);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncCart = () => setCart(readCart());
    const syncSaved = () => setSaved(readSaved());
    const syncStorage = () => {
      syncCart();
      syncSaved();
    };
    window.addEventListener('taa-shop-cart', syncCart);
    window.addEventListener('taa-shop-saved', syncSaved);
    window.addEventListener('storage', syncStorage);
    return () => {
      window.removeEventListener('taa-shop-cart', syncCart);
      window.removeEventListener('taa-shop-saved', syncSaved);
      window.removeEventListener('storage', syncStorage);
    };
  }, []);

  useEffect(() => {
    if (!isShopLoggedIn()) return undefined;
    fetchShopCollections()
      .then((data) => {
        const slugs = [
          ...(data.saved || []).map((item) => item.slug),
          ...(data.collections || []).flatMap((col) => (col.items || []).map((item) => item.slug)),
        ].filter(Boolean);
        if (slugs.length) {
          writeSaved([...new Set([...readSaved(), ...slugs])]);
        }
      })
      .catch(() => {});
    return undefined;
  }, []);

  const addToCart = (product, qty = 1) => {
    if (product?.is_sold_out) return;
    const next = readCart();
    const existing = next.find((item) => item.slug === product.slug);
    const amount = Math.max(1, Number(qty) || 1);
    if (existing) {
      existing.qty += amount;
      existing.image_url = existing.image_url || product.image_url || '';
      existing.compare_at_price = existing.compare_at_price || product.compare_at_price || null;
      existing.cooperative_name = existing.cooperative_name || product.cooperative_name || '';
    }
    else {
      next.push({
        slug: product.slug,
        title: product.title,
        price: product.price,
        currency: product.currency || 'MAD',
        qty: amount,
        image_url: product.image_url || '',
        compare_at_price: product.compare_at_price || null,
        cooperative_name: product.cooperative_name || '',
        category_slug: product.category_slug || '',
      });
    }
    writeCart(next);
    setCart(next);
    prefetchCheckoutQuote(next).catch(() => {});
    trackMetaAddToCart(product, amount);
  };

  const removeFromCart = (slug) => {
    const next = readCart().filter((item) => item.slug !== slug);
    writeCart(next);
    setCart(next);
  };

  const setCartQty = (slug, qty) => {
    if (qty < 1) {
      removeFromCart(slug);
      return;
    }
    const next = readCart().map((item) => (item.slug === slug ? { ...item, qty } : item));
    writeCart(next);
    setCart(next);
  };

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.qty, 0), [cart]);

  return (
    <ShopI18nContext.Provider value={i18n}>
      {isHome && (
      <SeoHead
        title={t.homeSeoTitle}
        description={t.homeSeoDesc}
        keywords="marketplace coopératives marocaines, acheter produits coopératives Maroc, Taaouniaty"
        url={buildAbsoluteUrl('/marketplace')}
        language={language === 'ar' ? 'ar' : language === 'en' ? 'en' : 'fr'}
        jsonLd={buildMarketplaceHomeJsonLd()}
      />
      )}
      <div className={`shop-shell ${isHome ? 'shop-shell--home' : ''}`}>
        <MarketplaceShell
        search={search}
        onSearch={setSearch}
        onSearchSubmit={goSearch}
        cartCount={cartCount}
        savedCount={saved.length}
        showSearch={!isHome && !isProduct && !isCoopShop && !isBrowse && !isDeals && !isCart && !isSaved && !isAccount}
        home={isHome}
        product={isProduct || isCoopShop || isBrowse || isDeals || isCart || isSaved || isAccount}
        hideProof={isCart || isAccount}
        socialProof={socialProof.length ? socialProof : (catalog.social_proof || catalog.socialProof || [])}
      >
        <Routes>
          <Route path="/" element={<ExploreView catalog={catalog} stories={coopStories} />} />
          <Route path="saved" element={<SavedView catalog={catalog} />} />
          <Route path="browse" element={<BrowseView catalog={catalog} query={search} />} />
          <Route path="browse/:categorySlug" element={<BrowseView catalog={catalog} query={search} />} />
          <Route path="deals" element={<DealsView catalog={catalog} query={search} />} />
          <Route path="deals/:categorySlug" element={<DealsView catalog={catalog} query={search} />} />
          <Route path="cart" element={<CartView items={cart} catalog={catalog} onRemove={removeFromCart} onQty={setCartQty} />} />
          <Route path="account" element={<AccountView catalog={catalog} />} />
          <Route path="p/:slug" element={<ProductDetail onAdd={addToCart} catalog={catalog} stories={coopStories} />} />
          <Route path="c/:slug" element={<CoopShopView catalog={catalog} stories={coopStories} />} />
        </Routes>
      </MarketplaceShell>
      </div>
    </ShopI18nContext.Provider>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3.1 3.4 10.2a1.1 1.1 0 0 0-.4.8V20a1.5 1.5 0 0 0 1.5 1.5h5.1V14.6h5.8V21.5h5.1A1.5 1.5 0 0 0 22 20v-9a1.1 1.1 0 0 0-.4-.8L12 3.1Z" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <rect x="3.2" y="3.2" width="7.6" height="7.6" rx="1.8" />
      <rect x="13.2" y="3.2" width="7.6" height="7.6" rx="1.8" />
      <rect x="3.2" y="13.2" width="7.6" height="7.6" rx="1.8" />
      <rect x="13.2" y="13.2" width="7.6" height="7.6" rx="1.8" />
    </svg>
  );
}
function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M7.1 6.2h13.2a.9.9 0 0 1 .9 1.1l-1.5 7.6a2.4 2.4 0 0 1-2.4 1.9H9.3a2.4 2.4 0 0 1-2.4-1.8L4.8 4.8H2.6a1 1 0 1 1 0-2h2.8c.9 0 1.6.6 1.8 1.4Z" />
      <circle cx="9.2" cy="20.2" r="1.7" />
      <circle cx="17.6" cy="20.2" r="1.7" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M20.7 12.8 12.2 4.3A2.2 2.2 0 0 0 10.6 3.6H4.8A1.8 1.8 0 0 0 3 5.4v5.8c0 .5.2 1 .6 1.4l8.5 8.5a2.2 2.2 0 0 0 3.1 0l5.5-5.5a2.2 2.2 0 0 0 0-3.1ZM7.6 9.2a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
      />
    </svg>
  );
}
function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5.2 19.4c.6-3.4 3.3-5.4 6.8-5.4s6.2 2 6.8 5.4a1.3 1.3 0 0 1-1.3 1.6H6.5a1.3 1.3 0 0 1-1.3-1.6Z" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16.2 16.2 21 21" strokeLinecap="round" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 8h16M4 16h16" strokeLinecap="round" />
      <circle cx="9" cy="8" r="2.2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="16" r="2.2" fill="currentColor" stroke="none" />
    </svg>
  );
}
function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
